import React, { FC, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { userAPI } from '../UserAPI';
import Section from '../components/Section';
import { ChecklistRunner } from '../components/ChecklistRunner';
import ChecklistAssetContext from '../components/ChecklistAssetContext';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import ReactApexChart from 'react-apexcharts';
import { useCustomerTheme } from '../Theme';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import 'dayjs/locale/de';
import { DocumentSlot } from '../components/DocumentSlot';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGuideAction } from '../guide/useGuideAction';
import { useUrlPageState } from '../utilities/dataTablePagination';
import { useDrag, useDrop } from 'react-dnd';
import { ItemTypes } from './ItemTypes';
import update from 'immutability-helper';
import { and, Checklist, EntityQuery, from, is, or, Query, Role, RoleType } from '@the-real-insight/tri-model';
import { useModalContext } from '../components/ModalProvider';
import { qualificationRoleName } from '../administration/operator-responsibility/legalDisplay';
import { useRoleAccess, viewerMayExecuteHumanTask } from '../hooks/useRoleAccess';
import { ProcessInstanceDocumentReferences } from '../components/ProcessInstanceDocumentReferences';
import { ProcessInstanceDataPool } from '../components/ProcessInstanceDataPool';

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Building,
    Camera,
    Check,
    ChevronDown,
    ChevronRight,
    ChevronsUpDown,
    Clock,
    ExternalLink,
    FileText,
    Folder,
    HardDrive,
    Layers,
    ListChecks,
    ListPlus,
    Loader2,
    Mail,
    MapPin,
    MessageSquare,
    Pause,
    Pencil,
    PenLine,
    Play,
    RefreshCw,
    Repeat,
    Rocket,
    Rss,
    Settings,
    Star,
    Trash2,
    UserPlus,
    UsersRound,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { DataTable } from '@/components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

dayjs.extend(relativeTime);

/** Akzentfarbe des Kunden für Symbole; fällt auf das Primary-Token zurück. */
const CUSTOMER_ACCENT = 'text-[color:var(--customer-primary-dark,hsl(var(--primary)))]';

function formatProcessTimestamp(v: unknown): string {
    if (v == null) return '–';
    try {
        const d = dayjs(v as any);
        return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : String(v);
    } catch {
        return String(v);
    }
}

/**
 * Best-effort label for who performed a history step (tri-server may send `user`, `performedBy`, etc.).
 */
function formatProcessHistoryActor(row: any): string {
    if (row == null) return '';
    const direct =
        row.userEmail ??
        row.performedByEmail ??
        row.assigneeEmail ??
        row.actorEmail ??
        row.completedByEmail;
    if (typeof direct === 'string' && direct.trim() !== '') {
        return direct.trim();
    }
    const u =
        row.user ??
        row.performedBy ??
        row.actor ??
        row.assignee ??
        row.completedBy ??
        row.completedByUser;
    if (u == null || u === '') return '';
    if (typeof u === 'string') return u.trim();
    if (typeof u === 'object') {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
        if (name) return name;
        if (u.email != null && String(u.email).trim() !== '') return String(u.email).trim();
        if (u.name != null && String(u.name).trim() !== '') return String(u.name).trim();
        if (u.displayName != null && String(u.displayName).trim() !== '') return String(u.displayName).trim();
    }
    return '';
}

function normalizeBpmWorkItemId(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint') {
        return null;
    }
    const id = String(value).trim();
    return id !== '' ? id : null;
}

/** Server attaches `definition: { name, version, createdAt }`; legacy may still have `definitionId`. */
function resolveProcessDefinitionLabel(rowOrInst: any): string {
    const d = rowOrInst?.definition;
    if (d?.name) {
        return d.version != null && d.version !== '' ? `${d.name} (v${d.version})` : String(d.name);
    }
    return rowOrInst?.definitionId != null && rowOrInst.definitionId !== ''
        ? String(rowOrInst.definitionId)
        : rowOrInst?.workflowId != null
          ? String(rowOrInst.workflowId)
          : '–';
}

/** Matches `processHumanTaskState.COMPLETED` — excluded from the instance worklist. */
function isHumanTaskRowCompleted(task: any): boolean {
    return String(task?.status ?? '').toUpperCase() === 'COMPLETED';
}

/**
 * „Startende/r Benutzer:in / aktuelle/r Benutzer:in“ einer Aufgabe — dieselbe
 * Auflösungsreihenfolge wie im Bestand (Tabelle und Detailansicht teilten sie
 * wortgleich).
 */
function taskUserLabel(row: any): string {
    const createdEntry = (row?.history as any[])?.find((h: any) => h.action === 'created' || h.action === 'start');
    const startU = row?.startingUser ?? row?.createdBy ?? createdEntry?.user ?? (row?.history?.[0] as any)?.user;
    const currU = row?.currentUser ?? row?.assigneeUser ?? row?.assignee ?? (row?.history as any[])?.filter((h: any) => h.user)?.[(row?.history as any[])?.length - 1]?.user;
    const startStr = startU ? ([startU?.firstName, startU?.lastName].filter(Boolean).join(' ') || startU?.email || '') : '';
    const currStr = currU ? ([currU?.firstName, currU?.lastName].filter(Boolean).join(' ') || currU?.email || '') : '';
    return [startStr, currStr].filter(Boolean).join(' / ');
}

/** DIN-276-Auswahl: Option des abgelösten ComboBox-Shims. */
type Din276Option = { key: string; text: string; data?: any };

type ProcessInstanceTimelineInterval = 'today' | 'd10' | 'd30' | 'd90';

/** Gesamtstatus timeline bar chart: fixed pixel height so layout does not shift when interval or pie filter changes. */
const PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT = 380;

function getProcessInstanceTimelineWindow(
    mode: ProcessInstanceTimelineInterval,
): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
    const end = dayjs();
    switch (mode) {
        case 'today':
            return { start: end.startOf('day'), end };
        case 'd10':
            return { start: end.subtract(9, 'day').startOf('day'), end };
        case 'd30':
            return { start: end.subtract(29, 'day').startOf('day'), end };
        case 'd90':
            return { start: end.subtract(89, 'day').startOf('day'), end };
        default:
            return { start: end.startOf('day'), end };
    }
}

function parseInstanceCreatedMs(inst: any): number | null {
    const v = inst?.createdAt;
    if (v == null) return null;
    const t = dayjs(v).valueOf();
    return Number.isFinite(t) ? t : null;
}

function buildProcessInstanceTimelineBuckets(
    instances: any[],
    mode: ProcessInstanceTimelineInterval,
): { categories: string[]; counts: number[] } {
    const { start, end } = getProcessInstanceTimelineWindow(mode);
    const filtered = instances.filter((inst) => {
        const t = parseInstanceCreatedMs(inst);
        if (t == null) return false;
        return t >= start.valueOf() && t <= end.valueOf();
    });

    if (mode === 'today') {
        const dayStart = start.startOf('day');
        const hours: dayjs.Dayjs[] = [];
        for (let h = 0; h < 24; h++) {
            hours.push(dayStart.add(h, 'hour'));
        }
        const categories = hours.map((h) => h.format('HH:mm'));
        const counts = hours.map((hour) => {
            const hs = hour.valueOf();
            const he = hour.add(1, 'hour').valueOf();
            return filtered.filter((inst) => {
                const t = parseInstanceCreatedMs(inst);
                return t != null && t >= hs && t < he;
            }).length;
        });
        return { categories, counts };
    }

    const days: dayjs.Dayjs[] = [];
    let cursor = start.startOf('day');
    const endDay = end.endOf('day');
    while (cursor.isBefore(endDay) || cursor.isSame(endDay, 'day')) {
        days.push(cursor);
        cursor = cursor.add(1, 'day');
    }

    const categories = days.map((d) => d.format('DD.MM.YYYY'));
    const counts = days.map((day) => {
        const ds = day.startOf('day').valueOf();
        const de = day.endOf('day').valueOf();
        return filtered.filter((inst) => {
            const t = parseInstanceCreatedMs(inst);
            return t != null && t >= ds && t <= de;
        }).length;
    });
    return { categories, counts };
}

function aggregateProcessInstancesByDefinition(instances: any[]): Record<string, number> {
    const m: Record<string, number> = {};
    for (const inst of instances) {
        const label = resolveProcessDefinitionLabel(inst);
        const key = label === '–' ? '–' : label;
        m[key] = (m[key] ?? 0) + 1;
    }
    return m;
}

/** Process run timeline: prefers `processHistory` (same payload as `engine.history` on tri-server). */

function normalizeProcessHistory(detail: any): any[] {
    if (!detail) return [];
    const unwrap = (h: unknown): any[] => {
        if (h == null) return [];
        if (Array.isArray(h)) return h;
        if (typeof h === 'object' && Array.isArray((h as any).entries)) return (h as any).entries;
        return [];
    };
    const fromTop = unwrap(detail.processHistory);
    if (fromTop.length > 0) return fromTop;
    const fromEngine = unwrap(detail.engine?.history);
    if (fromEngine.length > 0) return fromEngine;
    if (Array.isArray(detail.processInstanceHistory)) return detail.processInstanceHistory;
    return [];
}

/**
 * Build a map { parent.subProcess nodeId -> child detail } from a
 * ProcessInstanceDetailResult's `children` array.
 */
const buildChildDetailByNodeId = (
    children: any[] | undefined,
): Record<string, any> => {
    const map: Record<string, any> = {};
    if (!Array.isArray(children)) return map;
    for (const child of children) {
        const nodeId = child?.parentNodeId;
        const detail = child?.detail;
        if (!nodeId || !detail) continue;
        if ((detail as any).notFound || (detail as any).forbidden) continue;
        map[String(nodeId)] = detail;
    }
    return map;
};

type HistoryRowExt = Record<string, any> & {
    _depth: number;
    _rowKey: string;
    _hasChildHistory: boolean;
    _childInstanceId?: string;
};

/**
 * Flatten a process history with optional inlining of child process histories
 * when their anchor row is expanded.
 */
const flattenHistoryWithChildren = (
    history: any[] | undefined,
    childByNodeId: Record<string, any>,
    expandedRowKeys: Set<string>,
    depth = 0,
    parentRowKey = '',
): HistoryRowExt[] => {
    const out: HistoryRowExt[] = [];
    if (!Array.isArray(history)) return out;
    const sortedHistory = [...history].sort(
        (a: any, b: any) => (Number(a?.seq) || 0) - (Number(b?.seq) || 0),
    );
    const seenNodeAnchors = new Set<string>();
    for (const row of sortedHistory) {
        const nodeId = row?.nodeId != null ? String(row.nodeId) : '';
        const childDetail = nodeId ? childByNodeId[nodeId] : null;
        const isAnchor = !!childDetail && nodeId !== '' && !seenNodeAnchors.has(nodeId);
        if (isAnchor) seenNodeAnchors.add(nodeId);
        const rowKey = `${parentRowKey}/${row?._id ?? row?.seq ?? out.length}`;
        const childHistory = normalizeProcessHistory(childDetail);
        const hasChildHistory = isAnchor && childHistory.length > 0;
        const childInstanceId = isAnchor
            ? String(childDetail?.instance?._id ?? childDetail?.instance?.id ?? '')
            : undefined;
        out.push({
            ...row,
            _depth: depth,
            _rowKey: rowKey,
            _hasChildHistory: hasChildHistory,
            _childInstanceId: childInstanceId,
        });
        if (hasChildHistory && expandedRowKeys.has(rowKey)) {
            const grandChildMap = buildChildDetailByNodeId(childDetail?.children ?? []);
            out.push(
                ...flattenHistoryWithChildren(
                    childHistory,
                    grandChildMap,
                    expandedRowKeys,
                    depth + 1,
                    rowKey,
                ),
            );
        }
    }
    return out;
};

/** Query param for deep-linking process instance detail in Process management (see ProcessManagementPanel). */
const PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM = 'processInstanceId';
/** Deep link onto one running checklist — what the phase's open tasks link to. */
export const PROCESS_PANEL_CHECKLIST_INSTANCE_ID_PARAM = 'checklistInstanceId';

/** Temporary trace logs — search `checklist-def` and delete when done debugging. */

interface Properties {
}

/** Karten-Optik der ziehbaren Checklisten-Zeilen (vormals Inline-Styles). */
const FIELD_ROW_CLASS = 'cursor-move rounded-md border border-dashed border-border bg-card px-4 py-2';
const FIELD_ROW_SELECTED_CLASS = 'cursor-move rounded-md border border-solid border-foreground/40 bg-accent px-4 py-2';

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: React.ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                : sorted === 'desc'
                    ? <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                    : <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

/** Leerzustand einer Tabelle: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/** Leerzustand eines Bereichs (außerhalb einer Tabelle). */
function PanelEmptyState({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
    return (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/**
 * Sternebewertung — ersetzt den Fluent-`Rating`-Shim eins zu eins (gleiche
 * Sternzahl, gleiche Klickbarkeit, `allowZeroStars`). Die goldene Füllung
 * bekommt eine Kontur im warning-Token, damit der Stern die 3:1 für
 * nicht-textliche Elemente erreicht.
 */
function StarRating(properties: {
    value: number;
    max: number;
    readOnly?: boolean;
    onChange?: (value: number) => void;
    label: string;
    optionLabel?: (value: number) => string;
}) {
    const stars = Array.from({ length: properties.max }, (_, index) => index);

    if (properties.readOnly) {
        return (
            <div role="img" aria-label={properties.label} className="inline-flex items-center gap-0.5">
                {stars.map((index) => (
                    <Star
                        key={index}
                        aria-hidden
                        className={cn(
                            'h-4 w-4',
                            index < properties.value ? 'fill-[#eab308] text-warning' : 'text-muted-foreground',
                        )}
                    />
                ))}
            </div>
        );
    }

    return (
        <div
            role="slider"
            aria-label={properties.label}
            aria-valuemin={0}
            aria-valuemax={properties.max}
            aria-valuenow={properties.value}
            className="inline-flex items-center gap-0.5"
        >
            {stars.map((index) => (
                <button
                    key={index}
                    type="button"
                    aria-label={properties.optionLabel?.(index + 1) ?? String(index + 1)}
                    className="rounded-sm p-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:p-3.5"
                    onClick={() => properties.onChange?.(index + 1)}
                >
                    <Star
                        aria-hidden
                        className={cn(
                            'h-4 w-4',
                            index < properties.value ? 'fill-[#eab308] text-warning' : 'text-muted-foreground',
                        )}
                    />
                </button>
            ))}
        </div>
    );
}

/** Symbol je Checklisten-Feldtyp — dieselben lucide-Symbole wie der abgelöste Icon-Shim. */
const CHECKLIST_FIELD_ICONS: Record<string, React.ComponentType<any>> = {
    photo: Camera,
    signature: PenLine,
    buildingCapture: Building,
    floorCapture: Layers,
    roomCapture: MapPin,
    assetCapture: Settings,
    loop: Repeat,
    group: UsersRound,
};

/** i18n-Schlüssel der Feldtyp-Bezeichnung, wo er vom Typnamen abweicht. */
const FIELD_TYPE_LABEL_KEYS: Record<string, string> = { photo: 'takePhoto' };

/** Vorschau-Zeile „Symbol + Bezeichnung“ eines Feldtyps in Palette und Editor. */
function FieldTypeHint({ type, label }: { type: string; label: string }) {
    const Icon = CHECKLIST_FIELD_ICONS[type];

    return (
        <div className="flex min-w-0 items-center gap-2">
            {Icon ? <Icon aria-hidden className={cn('h-4 w-4 shrink-0', CUSTOMER_ACCENT)} /> : null}
            <span className="min-w-0 break-words text-sm">{label}</span>
        </div>
    );
}
export interface CardProps {
    id: string
    text: string,
    type: string,
    moveField: (id: string, to: number) => void,
    findField: (id: string) => { index: number }
    select: (id: string) => void
}

export interface CheckItemTypeProps {
    type: string,
    text: string,
}

const CheckItemType: FC<CheckItemTypeProps> = memo(function Card({
    type,
    text,
}) {
    const { t } = useTranslation();
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: ItemTypes.TYPE,
            // Use `fieldType` so payload never collides with react-dnd drag kind / `type` metadata.
            // `__checklistPalette` must not be inferred from `text` — labels can match titles, be empty, or equal a type name.
            item: { fieldType: type, __checklistPalette: true },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: (_item, _monitor) => {
            },
        }),
        [type],
    );

    return (
        <div
            ref={(node) => drag(node)}
            className={cn('mt-2 w-full', FIELD_ROW_CLASS, isDragging && 'opacity-0')}
        >
            <div className="flex min-w-0 flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1 break-words text-sm">{text}</div>
                <div className="flex w-[150px] shrink-0 justify-start">
                    {(() => {
                        switch (type) {
                            case 'text': return <Textarea disabled rows={2} value={t('processPanel.sampleText')} readOnly aria-hidden tabIndex={-1} />;
                            case 'integer': return <Input disabled value={t('processPanel.sampleNumber')} readOnly aria-hidden tabIndex={-1} />;
                            case 'decimal': return <Input disabled value={t('processPanel.sampleDecimal')} readOnly aria-hidden tabIndex={-1} />;
                            case 'OKNotOK': return (
                                <div className="flex items-center gap-2">
                                    <Switch checked={false} disabled aria-hidden tabIndex={-1} />
                                    <span className="text-sm">{t('processPanel.notOk')}</span>
                                </div>
                            );
                            case 'DoneNotDone': return (
                                <div className="flex items-center gap-2">
                                    <Switch checked={false} disabled aria-hidden tabIndex={-1} />
                                    <span className="text-sm">{t('processPanel.notDone')}</span>
                                </div>
                            );
                            case 'rating': return (
                                <StarRating
                                    value={3}
                                    max={5}
                                    readOnly
                                    label={t('processPanel.ratingStars', { value: 3, max: 5 })}
                                />
                            );
                            default: {
                                const label = t(`processPanel.${FIELD_TYPE_LABEL_KEYS[type] ?? type}`);
                                return CHECKLIST_FIELD_ICONS[type]
                                    ? <FieldTypeHint type={type} label={label} />
                                    : null;
                            }
                        }
                    })()}
                </div>
            </div>
        </div>
    )
});

interface DropZoneProperties {
    onDrop: (item: any) => void;
}

const CONTAINER_TYPES = ['buildingCapture', 'floorCapture', 'roomCapture', 'assetCapture', 'loop', 'group'];
const CAPTURE_TYPES = ['buildingCapture', 'floorCapture', 'roomCapture', 'assetCapture'];

/** API may send e.g. `Loop` / `LOOP`; UI and checks use lowercase camelCase from CONTAINER_TYPES. */
function canonicalContainerFieldType(type: string | undefined): string | undefined {
    if (type === undefined || type === null) return undefined;
    const s = String(type);
    if (CONTAINER_TYPES.includes(s)) return s;
    const lower = s.toLowerCase();
    const found = CONTAINER_TYPES.find((c) => c.toLowerCase() === lower);
    if (found) return found;
    // Some backends use different enum names for the same container kind.
    if (['repeat', 'foreach', 'iteration', 'iter'].includes(lower)) return 'loop';
    return undefined;
}

function _isChecklistContainerFieldType(type: string | undefined): boolean {
    return canonicalContainerFieldType(type) !== undefined;
}

/**
 * Discriminator for checklist field rows. Some APIs put the real kind in `fieldType` (e.g. Loop) and a generic `type`
 * (e.g. object); prefer whichever resolves to a known checklist/container kind.
 */
function fieldKind(f: any): string | undefined {
    if (f == null || typeof f !== 'object') return undefined;
    const t = f.type != null && f.type !== '' ? String(f.type) : undefined;
    const ft = f.fieldType != null && f.fieldType !== '' ? String(f.fieldType) : undefined;
    const canonT = canonicalContainerFieldType(t);
    const canonFt = canonicalContainerFieldType(ft);
    if (canonFt !== undefined && canonT === undefined) return ft;
    if (canonT !== undefined) return t;
    return t ?? ft;
}

function isChecklistContainerField(f: any): boolean {
    return canonicalContainerFieldType(fieldKind(f)) !== undefined;
}

/** Nested body: UI uses `children`; API may store the same list under `items`, `elements`, or (legacy) `fields` on container nodes. */
function getChecklistFieldChildren(field: any): any[] {
    if (!field) return [];
    const c = field.children;
    const it = field.items;
    const isContainer = isChecklistContainerField(field);
    const fd = isContainer && Array.isArray(field.fields) ? field.fields : undefined;
    const el = isContainer && Array.isArray(field.elements) ? field.elements : undefined;
    const nonEmpty = [c, it, fd, el].find((a) => Array.isArray(a) && (a as any[]).length > 0) as any[] | undefined;
    if (nonEmpty) return nonEmpty;
    if (Array.isArray(c)) return c;
    if (Array.isArray(it)) return it;
    if (Array.isArray(fd)) return fd;
    if (Array.isArray(el)) return el;
    return [];
}

/** Ensure one canonical `children` array (and drop duplicate `items` / misplaced `fields` used as body). */
function normalizeChecklistFieldTree(fields: any[] | undefined): any[] {
    if (!Array.isArray(fields)) return [];
    return fields.map((f) => {
        if (!f || typeof f !== 'object') return f;
        const canon = canonicalContainerFieldType(fieldKind(f));
        if (!canon) return { ...f };
        const raw = getChecklistFieldChildren({ ...f, type: canon });
        const nextChildren = normalizeChecklistFieldTree(raw);
        const next: any = { ...f, type: canon, children: nextChildren };
        if (next.items !== undefined) delete next.items;
        if (next.elements !== undefined) delete next.elements;
        // Nested body is canonicalized to `children`; drop legacy `fields` used as container body (spread would keep it).
        if (next.fields !== undefined) delete next.fields;
        if (next.__checklistPalette !== undefined) delete next.__checklistPalette;
        return next;
    });
}

function resolveFieldsFromProcessOrChecklistRow(row: any): any[] {
    if (!row) return [];
    const raw = row.fields ?? row.data?.fields ?? row.data?.checklist?.fields;
    return Array.isArray(raw) ? raw : [];
}

/** After `{ ...container, children }`, stale `items` / legacy `fields` / `elements` can still win in `getChecklistFieldChildren`. */
function stripLegacyContainerBodyKeys(field: any): any {
    if (!field || typeof field !== 'object') return field;
    if (!isChecklistContainerField(field) || !Array.isArray(field.children)) return field;
    const u: any = { ...field };
    if (u.items !== undefined) delete u.items;
    if (u.fields !== undefined) delete u.fields;
    if (u.elements !== undefined) delete u.elements;
    return u;
}

/** Mirror nested bodies for APIs that read `items` and/or `elements` (loops often differ from group/capture). */
function checklistFieldsForApi(fields: any[]): any[] {
    if (!Array.isArray(fields)) return [];
    return fields.map((f) => {
        if (!f || typeof f !== 'object') return f;
        const canon = canonicalContainerFieldType(fieldKind(f));
        if (!canon) return f;
        const raw = getChecklistFieldChildren(f);
        const nested = checklistFieldsForApi(raw);
        const out: any = { ...f, type: canon, children: nested, items: nested };
        if (out.fields !== undefined) delete out.fields;
        if (out.elements !== undefined) delete out.elements;
        if (out.__checklistPalette !== undefined) delete out.__checklistPalette;
        if (canon === 'loop') {
            out.elements = nested;
        }
        return out;
    });
}

const PHOTO_IMAGE_RESOLUTION_DEFAULT = 'medium';

function defaultFieldsForPaletteType(type: string): Record<string, unknown> {
    if (type === 'photo') {
        return { imageResolution: PHOTO_IMAGE_RESOLUTION_DEFAULT };
    }
    if (type === 'loop') {
        return { minLoopCycles: 0 };
    }
    return {};
}

type FlattenedField = { field: any; flatIndex: number; parentErfassung?: any; parentFlatIndex?: number };
const flattenInstanceFields = (fields: any[]): FlattenedField[] => {
    const result: FlattenedField[] = [];
    const walk = (list: any[], parentFlatIndex?: number) => {
        (list || []).forEach((f) => {
            const flatIndex = result.length;
            const item: FlattenedField = { field: f, flatIndex, parentFlatIndex };
            result.push(item);
            if (isChecklistContainerField(f) && getChecklistFieldChildren(f).length) {
                walk(getChecklistFieldChildren(f), flatIndex);
            }
        });
    };
    walk(fields);
    return result;
};

const CI_COLORS = (theme: any) => [theme?.secondary ?? '#0078d4', theme?.tertiary ?? '#107c10', theme?.primary ?? '#5c2d91', theme?.secondaryLight ?? '#50e6ff', theme?.tertiaryLight ?? '#9fffa0', theme?.primaryLight ?? '#b4a7ff'];

const ChecklistInstanceForm = memo((props: {
    checklist: any;
    onUpdate: (updated: any) => void | Promise<void>;
    theme: any;
    t: (k: string, options?: any) => any;
}) => {
    const { checklist, onUpdate, theme, t } = props;
    const formId = useId();
    const data = useMemo(() => checklist?.data ?? [], [checklist?.data]);
    const flattened = useMemo(() => flattenInstanceFields(checklist?.fields ?? []), [checklist?.fields]);
    const colors = useMemo(() => CI_COLORS(theme), [theme]);
    const grouped = useMemo(() => {
        const out: { container?: FlattenedField; rows: FlattenedField[] }[] = [];
        let i = 0;
        while (i < flattened.length) {
            const row = flattened[i];
            const isErfassung = isChecklistContainerField(row.field) && getChecklistFieldChildren(row.field).length;
            if (isErfassung) {
                const children: FlattenedField[] = [];
                let j = i + 1;
                while (j < flattened.length && flattened[j].parentFlatIndex === row.flatIndex) {
                    children.push(flattened[j]);
                    j++;
                }
                out.push({ container: row, rows: [row, ...children] });
                i = j;
            } else {
                out.push({ rows: [row] });
                i++;
            }
        }
        return out;
    }, [flattened]);

    const getValue = (idx: number) => (data[idx] !== undefined ? data[idx] : (flattened[idx]?.field?.type === 'OKNotOK' || flattened[idx]?.field?.type === 'DoneNotDone' ? false : flattened[idx]?.field?.type === 'rating' ? 0 : ''));

    const setValue = useCallback((idx: number, value: any) => {
        const newData = [...(data || [])];
        while (newData.length <= idx) newData.push(undefined);
        newData[idx] = value;
        onUpdate({ ...checklist, data: newData });
    }, [checklist, data, onUpdate]);

    const renderRow = (item: FlattenedField, skipRowBar?: boolean) => {
        const { field, flatIndex, parentFlatIndex } = item;
        const rowColor = colors[flatIndex % colors.length];
        const isNested = parentFlatIndex !== undefined;
        const fieldId = `${formId}-field-${flatIndex}`;
        const containerCanon = canonicalContainerFieldType(fieldKind(field));
        const captureType = CAPTURE_TYPES.includes(field.type) ? field.type : undefined;

        return (
            <div key={flatIndex} className="mt-2 flex items-start">
                {!skipRowBar && (
                    <div
                        aria-hidden
                        className="mr-2 w-1 min-w-[4px] self-stretch"
                        style={{ backgroundColor: rowColor }}
                    />
                )}
                <div className={cn('flex min-w-0 flex-1 flex-wrap gap-2', isNested && 'ml-4')}>
                    <Label htmlFor={fieldId} className="min-w-[120px] flex-1 break-words font-normal">
                        {field.text}
                    </Label>
                    <div className="w-full shrink-0 sm:w-[300px]">
                        {field.type === 'text' && (
                            <Textarea id={fieldId} value={getValue(flatIndex) ?? ''} onChange={(event) => setValue(flatIndex, event.target.value)} />
                        )}
                        {field.type === 'integer' && (
                            <Input id={fieldId} type="number" value={String(getValue(flatIndex) ?? '')} onChange={(event) => setValue(flatIndex, event.target.value === '' ? undefined : Number(event.target.value))} />
                        )}
                        {field.type === 'decimal' && (
                            <Input id={fieldId} value={String(getValue(flatIndex) ?? '')} onChange={(event) => setValue(flatIndex, event.target.value === '' ? undefined : parseFloat(event.target.value))} />
                        )}
                        {field.type === 'OKNotOK' && (
                            <div className="flex items-center gap-2">
                                <Switch id={fieldId} checked={!!getValue(flatIndex)} onCheckedChange={(checked) => setValue(flatIndex, checked)} />
                                <span className="text-sm">{getValue(flatIndex) ? t('processPanel.ok') : t('processPanel.notOk')}</span>
                            </div>
                        )}
                        {field.type === 'DoneNotDone' && (
                            <div className="flex items-center gap-2">
                                <Switch id={fieldId} checked={!!getValue(flatIndex)} onCheckedChange={(checked) => setValue(flatIndex, checked)} />
                                <span className="text-sm">{getValue(flatIndex) ? t('processPanel.done') : t('processPanel.notDone')}</span>
                            </div>
                        )}
                        {field.type === 'rating' && (
                            <StarRating
                                value={Number(getValue(flatIndex)) || 0}
                                max={5}
                                label={field.text ?? t('processPanel.rating')}
                                optionLabel={(value) => t('processPanel.ratingSelect', { value, max: 5 })}
                                onChange={(value) => setValue(flatIndex, value)}
                            />
                        )}
                        {(field.type === 'photo' || field.type === 'signature' || captureType || containerCanon === 'loop' || containerCanon === 'group') && (() => {
                            const kind = containerCanon ?? captureType ?? field.type;
                            return (
                                <Button variant="ghost" className="gap-2" onClick={() => { }}>
                                    <FieldTypeHint type={kind} label={t(`processPanel.${FIELD_TYPE_LABEL_KEYS[kind] ?? kind}`)} />
                                </Button>
                            );
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-w-0">
            {grouped.map((g, gi) =>
                g.container ? (
                    <div key={gi} className="mt-2 flex items-start">
                        <div
                            aria-hidden
                            className="mr-2 w-1 min-w-[4px] shrink-0 self-stretch"
                            style={{ backgroundColor: colors[g.container.flatIndex % colors.length] }}
                        />
                        <div className="flex min-w-0 flex-1 flex-col items-stretch">
                            {g.rows.map((row, ri) => renderRow(row, ri === 0))}
                        </div>
                    </div>
                ) : (
                    <div key={gi} className="min-w-0">{g.rows.map((row) => renderRow(row))}</div>
                )
            )}
        </div>
    );
});
/** Field kind from palette (`fieldType`) or existing row drag (`type` on field object). */
function dragItemFieldType(item: any): string | undefined {
    if (!item) return undefined;
    return item.fieldType ?? item.type ?? item.item?.fieldType ?? item.item?.type;
}

/** Palette drags only — never infer from `text` (labels can duplicate, match i18n titles, or equal technical names). */
function isChecklistPaletteDragItem(item: any): boolean {
    return item?.__checklistPalette === true;
}

const canDropInContainer = (item: any, container?: any): boolean => {
    const fieldType = dragItemFieldType(item);
    if (!fieldType) return false;
    const containerType = canonicalContainerFieldType(fieldKind(container));
    if (!containerType) return false;
    if (fieldType === 'group' && (containerType === 'loop' || CAPTURE_TYPES.includes(containerType))) return false;
    if (containerType === 'group') return true;
    if (containerType === 'loop') {
        // Loop body: leaf fields + capture blocks; no nested loop/group.
        if (fieldType === 'loop' || fieldType === 'group') return false;
        return true;
    }
    return !CONTAINER_TYPES.includes(fieldType);
};

const removeFieldFromTree = (fields: any[], fieldToRemove: any): any[] => {
    return fields
        .filter((f) => f !== fieldToRemove)
        .map((f) => {
            if (isChecklistContainerField(f)) {
                const childList = getChecklistFieldChildren(f);
                const nextChildren = removeFieldFromTree(childList, fieldToRemove);
                if (nextChildren === childList) return f;
                const updated: any = { ...f, children: nextChildren };
                if (updated.items !== undefined) delete updated.items;
                if (updated.fields !== undefined) delete updated.fields;
                if (updated.elements !== undefined) delete updated.elements;
                return updated;
            }
            return f;
        });
};

/** Path: first index is root field index; each following index is into the previous field's `children` array. */
function getFieldAtPath(fields: any[], path: number[]): any | undefined {
    if (!path.length) return undefined;
    let cur: any = fields[path[0]];
    if (cur === undefined) return undefined;
    for (let i = 1; i < path.length; i++) {
        const branch = getChecklistFieldChildren(cur);
        if (!Array.isArray(branch) || branch[path[i]] === undefined) return undefined;
        cur = branch[path[i]];
    }
    return cur;
}

/** Immutable update of the field at `path` (same path shape as {@link getFieldAtPath}). */
function updateFieldAtPath(fields: any[], path: number[], updater: (f: any) => any): any[] {
    if (!path.length) return fields;
    const [head, ...rest] = path;
    if (rest.length === 0) {
        return fields.map((f, i) => (i === head ? stripLegacyContainerBodyKeys(updater(f)) : f));
    }
    return fields.map((f, i) => {
        if (i !== head) return f;
        if (!isChecklistContainerField(f)) return f;
        const childList = getChecklistFieldChildren(f);
        const nextChildren = updateFieldAtPath(childList, rest, updater);
        if (nextChildren === childList) return f;
        const updated: any = { ...f, children: nextChildren };
        if (updated.items !== undefined) delete updated.items;
        if (updated.fields !== undefined) delete updated.fields;
        if (updated.elements !== undefined) delete updated.elements;
        return updated;
    });
}

/**
 * Insert into a container identified by path (not by reference — Entity/API clones break `f === container`).
 * Signature: (fields, pathToContainer, newField, atIndex) — path is root index then children indices.
 */
function addFieldToContainer(fields: any[], pathToContainer: number[], newField: any, atIndex: number): any[] {
    if (!pathToContainer.length) return fields;
    const [head, ...rest] = pathToContainer;
    if (rest.length === 0) {
        const f = fields[head];
        if (!f) return fields;
        const childList = [...getChecklistFieldChildren(f)];
        childList.splice(Math.min(atIndex, childList.length), 0, newField);
        const updated: any = { ...f, children: childList };
        if (updated.items !== undefined) delete updated.items;
        if (updated.fields !== undefined) delete updated.fields;
        if (updated.elements !== undefined) delete updated.elements;
        return fields.map((field, i) => (i === head ? updated : field));
    }
    const f = fields[head];
    if (!f) return fields;
    const nextChildren = addFieldToContainer(getChecklistFieldChildren(f), rest, newField, atIndex);
    const updated: any = { ...f, children: nextChildren };
    if (updated.items !== undefined) delete updated.items;
    if (updated.fields !== undefined) delete updated.fields;
    if (updated.elements !== undefined) delete updated.elements;
    return fields.map((field, i) => (i === head ? updated : field));
}

const insertFieldAt = (fields: any[], newField: any, atIndex: number): any[] => {
    const result = [...fields];
    result.splice(atIndex, 0, newField);
    return result;
};

const updateFieldInTree = (fields: any[], fieldToUpdate: any, updater: (f: any) => any): any[] => {
    return fields.map((f) => {
        if (f === fieldToUpdate) return stripLegacyContainerBodyKeys(updater(f));
        if (isChecklistContainerField(f)) {
            const childList = getChecklistFieldChildren(f);
            const nextChildren = updateFieldInTree(childList, fieldToUpdate, updater);
            if (nextChildren === childList) return f;
            const updated: any = { ...f, children: nextChildren };
            if (updated.items !== undefined) delete updated.items;
            if (updated.fields !== undefined) delete updated.fields;
            if (updated.elements !== undefined) delete updated.elements;
            return updated;
        }
        return f;
    });
};

interface ChecklistFieldRowProps {
    field: any;
    fieldPath: number[];
    showRootDropZone: boolean;
    selectedField: any;
    selectChecklistField: (field: any, path: number[] | null) => void;
    selectedChecklist: any;
    setSelectedChecklist: (c: any) => void;
    insertAtIndex: number;
    addToContainer: (fieldPath: number[], newField: any, atIndex: number) => void;
    addAtIndex: (newField: any, atIndex: number) => void;
    removeField: (f: any) => void;
    renderFieldPreview: (field: any) => any;
    t: (key: string, options?: any) => any;
}
export default function ProcessManagementPanel(_properties: Properties) {
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { pushError, pushSuccess, showDeleteDialog } = useModalContext() as any;
    const roleAccess = useRoleAccess();
    const tasksPagination = useUrlPageState('tasksPage');
    const processInstancesPagination = useUrlPageState('processInstancesPage');
    const processDefinitionsPagination = useUrlPageState('processDefinitionsPage');
    const processHistoryPagination = useUrlPageState('processHistoryPage');
    const worklistPagination = useUrlPageState('worklistPage');
    const [colors] = useState([theme.secondary, theme.tertiary, theme.primary, theme.secondaryLight, theme.tertiaryLight, theme.primaryLight]);
    const [tab, setTab] = useState('tasks') as any;
    const [tasks, setTasks] = useState([]) as any;
    const [checklistInstances, setChecklistInstances] = useState([]) as any;
    const [checklistInstancesLoaded, setChecklistInstancesLoaded] = useState(false);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [processDefinitions, setProcessDefinitions] = useState([]) as any;
    const [selectedTask, setSelectedTask] = useState() as any;
    const [taskComment, setTaskComment] = useState('');
    const [selectedChecklist, setSelectedChecklist] = useState() as any;
    const [selectedField, setSelectedField] = useState() as any;
    const [selectedFieldPath, setSelectedFieldPath] = useState<number[] | null>(null);
    const [checklistSaving, setChecklistSaving] = useState(false);
    const selectChecklistField = useCallback((field: any, path: number[] | null) => {
        setSelectedField(field);
        setSelectedFieldPath(path);
    }, []);
    const patchSelectedFieldInTree = useCallback(
        (updater: (f: any) => any) => {
            setSelectedChecklist((prev: any) => {
                const fields = prev?.fields ?? [];
                const newFields =
                    selectedFieldPath?.length
                        ? updateFieldAtPath(fields, selectedFieldPath, updater)
                        : updateFieldInTree(fields, selectedField, updater);
                return { ...prev, fields: newFields };
            });
            setSelectedField((prev: any) => (prev ? updater(prev) : prev));
        },
        [selectedFieldPath, selectedField],
    );
    const [roles, setRoles] = useState([]) as any;

    /**
     * Prefill from the address
     * (`?panel=processManagement&tab=processDefinitionsPlanning&guide=settings.processManagement.createChecklist&name=…&description=…`):
     * the same as the „Checkliste anlegen" button, only with name and description taken from the
     * address instead of the default name. Role and check fields remain a selection and their own
     * list, respectively.
     *
     * `ready` depends on the roles: the button is disabled without a role (a checklist without an
     * executing role could not be run), and the hook bypasses the button. While the roles are
     * still loading, it waits; if the tenant has none, it opens nothing. This is the registry
     * entry's `guideAction.requiresState` = `anyRole`.
     */
    useGuideAction(
        'settings.processManagement.createChecklist',
        (values) => {
            selectChecklistField(null, null);
            setSelectedChecklist(new Checklist({
                name: values.name ?? 'Neue Checkliste',
                description: values.description ?? '',
                role: roles[0]?.name ?? '',
                fields: [],
                checklistType: 'definition',
            }));
        },
        { panel: 'processManagement', tab: 'processDefinitionsPlanning', ready: roles.length > 0 },
    );
    const [overallStatusInstances, setOverallStatusInstances] = useState<any[]>([]);
    const [overallStatusInstancesLoading, setOverallStatusInstancesLoading] = useState(false);
    const [processInstanceTimelineInterval, setProcessInstanceTimelineInterval] =
        useState<ProcessInstanceTimelineInterval>('d30');
    /** Pie sector hover (Gesamtstatus): filter timeline bars to that process definition only. */
    const [pieHoveredDefinitionLabel, setPieHoveredDefinitionLabel] = useState<string | null>(null);
    const [startProcessMenuOpen, setStartProcessMenuOpen] = useState(false);
    const [startingProcessId, setStartingProcessId] = useState<string | null>(null);
    const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
    const [authentication] = useAtom(authenticationAtom) as any;
    const textEntry = useRef<HTMLInputElement>(null);
    const [din276Metadata, setDin276Metadata] = useState<any[]>([]);
    const [checklistRefreshTrigger, setChecklistRefreshTrigger] = useState(0);
    const [din276Filter, setDin276Filter] = useState('');
    const [din276PickerOpen, setDin276PickerOpen] = useState(false);
    const [bpmSchedules, setBpmSchedules] = useState<any[]>([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [scheduleToggling, setScheduleToggling] = useState<string | null>(null);
    // scheduleId → fire-history state. `events` undefined while the first
    // fetch is in flight; null after a failed fetch so we show an error row
    // instead of an infinite spinner.
    const [fireHistory, setFireHistory] = useState<Record<string, { events?: any[] | null; loading: boolean }>>({});
    const [expandedSchedules, setExpandedSchedules] = useState<Record<string, boolean>>({});
    const fireHistoryRef = useRef(fireHistory);
    const expandedSchedulesRef = useRef(expandedSchedules);
    useEffect(() => {
        fireHistoryRef.current = fireHistory;
    }, [fireHistory]);
    useEffect(() => {
        expandedSchedulesRef.current = expandedSchedules;
    }, [expandedSchedules]);
    const [bpmProcessInstances, setBpmProcessInstances] = useState<any[]>([]);
    const [bpmProcessInstancesLoading, setBpmProcessInstancesLoading] = useState(false);
    const [bpmProcessStatusFilter, setBpmProcessStatusFilter] = useState('');
    const [selectedBpmProcessDetail, setSelectedBpmProcessDetail] = useState<any | null>(null);
    const [bpmProcessDetailLoading, setBpmProcessDetailLoading] = useState(false);
    // Guards async detail/checklist responses so stale requests from a
    // previously opened process instance cannot update current state.
    const bpmProcessDetailRequestSeqRef = useRef(0);
    const activeBpmProcessDetailInstanceIdRef = useRef<string | null>(null);
    const cancelBpmChecklistLoadRef = useRef<(() => void) | null>(null);
    // workItemId → Checklist instance for the currently opened process
    // instance. Populated once per detail open so history rows can render the
    // "Open checklist" link without a per-row probe. Cleared when a different
    // instance opens.
    const [bpmInstanceChecklistsByWorkItem, setBpmInstanceChecklistsByWorkItem] = useState<Record<string, any>>({});
    // The checklist instance currently shown in the read-only viewer dialog
    // (opened from a history row's "Open checklist" link).
    const [viewingChecklistInstance, setViewingChecklistInstance] = useState<any | null>(null);
    /**
     * Sub-process expansion state for the *details* view's children tree.
     * Lives at the panel level so toggling persists across re-renders while
     * the dialog is open; reset whenever a different parent is opened so
     * stale ids don't leak across detail views.
     */
    const [detailExpandedRowIds, setDetailExpandedRowIds] = useState<Set<string>>(() => new Set());
    const toggleDetailRowExpanded = useCallback((rowId: string) => {
        setDetailExpandedRowIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    }, []);

    useEffect(() => {
        return () => {
            bpmProcessDetailRequestSeqRef.current += 1;
            activeBpmProcessDetailInstanceIdRef.current = null;
            if (cancelBpmChecklistLoadRef.current) {
                cancelBpmChecklistLoadRef.current();
                cancelBpmChecklistLoadRef.current = null;
            }
        };
    }, []);

    const bpmProcessDetailChatConversationId = useMemo(() => {
        if (!selectedBpmProcessDetail) return null;
        const inst = selectedBpmProcessDetail?.instance;
        const d = selectedBpmProcessDetail as any;
        const id =
            inst?.conversationId ??
            d?.conversation?._id ??
            d?.conversation?.id ??
            null;
        return id != null && String(id) !== '' ? String(id) : null;
    }, [selectedBpmProcessDetail]);

    const processPanelTabKeys = useMemo(
        () => ['overallStatus', 'tasks', 'processInstances', 'processDefinitionsPlanning', 'eventSchedules'] as const,
        [],
    );

    useEffect(() => {
        userAPI.getDIN276Metadata().then(setDin276Metadata).catch(() => setDin276Metadata([]));
    }, []);

    useEffect(() => {
        // SettingsPage swaps panels via a useState that lags by one render, so this
        // effect still fires on a navigation that is leaving the process panel
        // (e.g. clicking a deep-link to panel=agent). Skip the tab normalization
        // in that case — otherwise we'd overwrite the target panel's tab value.
        const panelParam = searchParams.get('panel');
        if (panelParam && panelParam !== 'processManagement') return;

        const tabParam = searchParams.get('tab');
        const processInstanceId = searchParams.get(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM)?.trim();
        if (processInstanceId) {
            setTab('processInstances');
            if (tabParam !== 'processInstances') {
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('tab', 'processInstances');
                        return next;
                    },
                    { replace: true },
                );
            }
            return;
        }
        if (tabParam && (processPanelTabKeys as readonly string[]).includes(tabParam)) {
            setTab(tabParam);
        } else {
            setTab('tasks');
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', 'tasks');
                    return next;
                },
                { replace: true },
            );
        }
    }, [searchParams, processPanelTabKeys, setSearchParams]);

    const toggleScheduleExpansion = useCallback(async (scheduleId: string) => {
        const alreadyExpanded = expandedSchedulesRef.current[scheduleId] === true;
        const nextExpanded = !alreadyExpanded;
        const nextExpandedSchedules = { ...expandedSchedulesRef.current, [scheduleId]: nextExpanded };
        expandedSchedulesRef.current = nextExpandedSchedules;
        setExpandedSchedules(nextExpandedSchedules);
        if (!nextExpanded) return;
        // First expansion: fetch if we don't have cached data yet.
        const historyState = fireHistoryRef.current[scheduleId];
        if (Array.isArray(historyState?.events) || historyState?.loading === true) return;
        const loadingState = { ...fireHistoryRef.current, [scheduleId]: { loading: true } };
        fireHistoryRef.current = loadingState;
        setFireHistory(loadingState);
        try {
            const events = await userAPI.getBpmScheduleFireHistory(
                authentication.customer,
                scheduleId,
                { limit: 50 },
            );
            const loadedState = { ...fireHistoryRef.current, [scheduleId]: { events, loading: false } };
            fireHistoryRef.current = loadedState;
            setFireHistory(loadedState);
        } catch (err) {
            console.error('Fire history fetch failed:', err);
            const failedState = { ...fireHistoryRef.current, [scheduleId]: { events: null, loading: false } };
            fireHistoryRef.current = failedState;
            setFireHistory(failedState);
        }
    }, [authentication.customer]);

    const loadSchedules = useCallback(() => {
        if (!authentication?.customer) return;
        setSchedulesLoading(true);
        userAPI.getBpmSchedules(authentication.customer)
            .then(setBpmSchedules)
            .catch(() => setBpmSchedules([]))
            .finally(() => setSchedulesLoading(false));
    }, [authentication?.customer]);

    useEffect(() => {
        if (tab === 'eventSchedules') loadSchedules();
    }, [tab, loadSchedules]);

    // Deep link into the checklist editor: ?panel=processManagement&checklistId=<id>
    // (used e.g. by the Betreiberverantwortung master-checklist view).
    const consumedChecklistIdRef = useRef<string | null>(null);
    useEffect(() => {
        const checklistId = searchParams.get('checklistId')?.trim();
        if (!checklistId || consumedChecklistIdRef.current === checklistId) return;
        consumedChecklistIdRef.current = checklistId;
        (async () => {
            try {
                const rows = await userAPI.query(from(Checklist.type).where(is('_id').equal(checklistId)));
                const row: any = rows?.[0];
                if (!row) return;
                const fields = normalizeChecklistFieldTree(resolveFieldsFromProcessOrChecklistRow(row));
                selectChecklistField(null, null);
                setSelectedChecklist({ ...row, fields });
            } catch (error) {
                console.warn('[checklist-def] deep-link load failed', error);
            }
        })();
    }, [searchParams, selectChecklistField]);

    // Deep link onto a running checklist: ?panel=processManagement&tab=tasks&checklistInstanceId=<id>
    // (used by the open tasks of a phase in the portfolio dashboard). The list is preferred over a
    // fetch — it is the same row a click in the table would select — with a direct read as the
    // fallback, so the link still opens the checklist for someone whose worklist does not carry it.
    const consumedInstanceIdRef = useRef<string | null>(null);
    useEffect(() => {
        const instanceId = searchParams.get(PROCESS_PANEL_CHECKLIST_INSTANCE_ID_PARAM)?.trim();
        if (!instanceId || consumedInstanceIdRef.current === instanceId) return;

        const fromList = (checklistInstances ?? []).find(
            (instance: any) => String(instance?._id) === instanceId,
        );

        if (fromList) {
            consumedInstanceIdRef.current = instanceId;
            setTab('tasks');
            selectTask(fromList);
            return;
        }

        if (!checklistInstancesLoaded) return;

        consumedInstanceIdRef.current = instanceId;
        (async () => {
            try {
                const rows = await userAPI.query(from(Checklist.type).where(is('_id').equal(instanceId)));
                const row: any = rows?.[0];
                if (!row) return;

                setTab('tasks');
                selectTask(row);
            } catch (error) {
                console.warn('[checklist-instance] deep-link load failed', error);
            }
        })();
        // `selectTask` is recreated on every render; the ref above is what keeps this to one run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, checklistInstances, checklistInstancesLoaded]);

    const loadBpmProcessInstances = useCallback(async () => {
        if (!authentication?.customer) return;
        setBpmProcessInstancesLoading(true);
        try {
            const items = await userAPI.getProcessInstances(authentication.customer, {
                status: bpmProcessStatusFilter || undefined,
                limit: 200,
            });
            setBpmProcessInstances(items ?? []);
        } catch (e) {
            pushError(e);
            setBpmProcessInstances([]);
        } finally {
            setBpmProcessInstancesLoading(false);
        }
    }, [authentication?.customer, bpmProcessStatusFilter, pushError]);

    useEffect(() => {
        if (tab === 'processInstances') loadBpmProcessInstances();
    }, [tab, loadBpmProcessInstances]);

    const loadOverallStatusInstances = useCallback(async () => {
        if (!authentication?.customer) return;
        setOverallStatusInstancesLoading(true);
        try {
            // Gesamtstatus = BPM process instances PLUS law-derived checklist
            // instances (tenant-wide, not viewer-role-scoped) as pseudo
            // instances — they share the definition/status/createdAt shape so
            // metrics, pie and timeline aggregate them together.
            const [items, lawChecklists] = await Promise.all([
                userAPI.getProcessInstances(authentication.customer, { limit: 5000 }),
                userAPI
                    .query(
                        from(Checklist.type).where(
                            and(is('checklistType').equal('instance'), is('lawDerived').equal(true)),
                        ),
                    )
                    .catch(() => []),
            ]);
            // creation time: custom fields (createdAt) are stripped by the
            // Checklist entity unmarshalling — fall back to the ObjectId
            // timestamp (first 4 bytes = unix seconds), which always survives.
            const objectIdTime = (id: any): string | null => {
                const hex = String(id ?? '');
                if (!/^[a-f0-9]{24}$/i.test(hex)) return null;
                return new Date(parseInt(hex.slice(0, 8), 16) * 1000).toISOString();
            };
            const checklistPseudoInstances = (Array.isArray(lawChecklists) ? lawChecklists : []).map(
                (checklist: any) => ({
                    _rowType: 'checklist',
                    _id: checklist._id,
                    definition: { name: checklist.name },
                    status: checklist.endTime ? 'COMPLETED' : 'RUNNING',
                    createdAt:
                        checklist.createdAt ?? checklist.startTime ?? objectIdTime(checklist._id),
                    assetName: checklist.assetName ?? null,
                    role: checklist.role,
                }),
            );
            setOverallStatusInstances([
                ...(Array.isArray(items) ? items : []),
                ...checklistPseudoInstances,
            ]);
        } catch (e) {
            pushError(e);
            setOverallStatusInstances([]);
        } finally {
            setOverallStatusInstancesLoading(false);
        }
    }, [authentication?.customer, pushError]);

    useEffect(() => {
        if (tab === 'overallStatus') loadOverallStatusInstances();
    }, [tab, loadOverallStatusInstances]);

    const openBpmProcessDetail = useCallback(
        async (row: any) => {
            const id = row?._id ?? row?.id;
            if (!id || !authentication?.customer) return;
            const processInstanceId = String(id);
            const detailRequestSeq = ++bpmProcessDetailRequestSeqRef.current;
            activeBpmProcessDetailInstanceIdRef.current = processInstanceId;
            if (cancelBpmChecklistLoadRef.current) {
                cancelBpmChecklistLoadRef.current();
                cancelBpmChecklistLoadRef.current = null;
            }
            setBpmProcessDetailLoading(true);
            setSelectedBpmProcessDetail(null);
            // Fresh detail = fresh expansion. Ids from the previous instance's
            // tree shouldn't accidentally pre-expand rows in this one.
            setDetailExpandedRowIds(new Set());
            setBpmInstanceChecklistsByWorkItem({});
            try {
                const detail = await userAPI.getProcessInstance(authentication.customer, processInstanceId);
                if (
                    bpmProcessDetailRequestSeqRef.current !== detailRequestSeq ||
                    activeBpmProcessDetailInstanceIdRef.current !== processInstanceId
                ) {
                    return;
                }
                setSelectedBpmProcessDetail(detail);
                // Fetch the checklist instances bound to any userTask in this
                // process so the history rows can render the "Open checklist"
                // link without a per-row API probe. Fire-and-forget; absence
                // of checklists just hides the column content.
                let checklistRequestCancelled = false;
                const cancelChecklistRequest = () => {
                    checklistRequestCancelled = true;
                };
                cancelBpmChecklistLoadRef.current = cancelChecklistRequest;
                userAPI
                    .getProcessInstanceChecklistInstances(authentication.customer, processInstanceId)
                    .then((list) => {
                        if (
                            checklistRequestCancelled ||
                            bpmProcessDetailRequestSeqRef.current !== detailRequestSeq ||
                            activeBpmProcessDetailInstanceIdRef.current !== processInstanceId
                        ) {
                            return;
                        }
                        const byWorkItem: Record<string, any> = {};
                        for (const inst of list ?? []) {
                            const wid = normalizeBpmWorkItemId((inst as any)?.bpmWorkItemId);
                            if (wid) byWorkItem[wid] = inst;
                        }
                        setBpmInstanceChecklistsByWorkItem(byWorkItem);
                    })
                    .catch((err) => {
                        if (
                            checklistRequestCancelled ||
                            bpmProcessDetailRequestSeqRef.current !== detailRequestSeq ||
                            activeBpmProcessDetailInstanceIdRef.current !== processInstanceId
                        ) {
                            return;
                        }
                        console.error('Failed to load checklist instances for process:', err);
                    })
                    .finally(() => {
                        if (cancelBpmChecklistLoadRef.current === cancelChecklistRequest) {
                            cancelBpmChecklistLoadRef.current = null;
                        }
                    });
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('tab', 'processInstances');
                        next.set(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM, processInstanceId);
                        return next;
                    },
                    { replace: true },
                );
            } catch (e) {
                if (
                    bpmProcessDetailRequestSeqRef.current !== detailRequestSeq ||
                    activeBpmProcessDetailInstanceIdRef.current !== processInstanceId
                ) {
                    return;
                }
                pushError(e);
                setSearchParams(
                    (prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM);
                        return next;
                    },
                    { replace: true },
                );
            } finally {
                if (
                    bpmProcessDetailRequestSeqRef.current === detailRequestSeq &&
                    activeBpmProcessDetailInstanceIdRef.current === processInstanceId
                ) {
                    setBpmProcessDetailLoading(false);
                }
            }
        },
        [authentication?.customer, pushError, setSearchParams],
    );

    /** Open detail from URL when `processInstanceId` is present and not already showing. */
    useEffect(() => {
        const id = searchParams.get(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM)?.trim();
        if (!id || !authentication?.customer) return;
        if (bpmProcessDetailLoading) return;
        const inst = selectedBpmProcessDetail?.instance;
        const loadedId = inst?._id ?? inst?.id ?? (selectedBpmProcessDetail as any)?._id;
        if (loadedId != null && String(loadedId) === id) return;
        openBpmProcessDetail({ _id: id });
    }, [
        searchParams,
        authentication?.customer,
        bpmProcessDetailLoading,
        selectedBpmProcessDetail,
        openBpmProcessDetail,
    ]);

    const handleDeleteProcessInstanceClick = useCallback(
        (row: any) => {
            const id = String(row?._id ?? row?.id ?? '');
            if (!id || !authentication?.customer) return;
            showDeleteDialog(
                t('processPanel.deleteProcessInstanceTitle'),
                id,
                async () => {
                    try {
                        await userAPI.deleteProcessInstance(authentication.customer, id);
                        setSelectedBpmProcessDetail((prev: any) => {
                            const inst = prev?.instance;
                            const pid = inst?._id ?? inst?.id ?? prev?._id ?? prev?.id;
                            if (pid != null && String(pid) === id) return null;
                            return prev;
                        });
                        setSearchParams(
                            (prev) => {
                                const next = new URLSearchParams(prev);
                                if (next.get(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM) === id) {
                                    next.delete(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM);
                                }
                                return next;
                            },
                            { replace: true },
                        );
                        await loadBpmProcessInstances();
                        await loadOverallStatusInstances();
                        pushSuccess(t('processPanel.deleteProcessInstanceSuccess'));
                    } catch (e) {
                        pushError(e);
                    }
                },
            );
        },
        [
            authentication?.customer,
            t,
            showDeleteDialog,
            loadBpmProcessInstances,
            loadOverallStatusInstances,
            pushError,
            pushSuccess,
            setSearchParams,
        ],
    );

    // Platform-admin-only: purge ALL of the customer's process instances. Guarded
    // by a type-the-customer-id confirmation because it is irreversible.
    const handleDeleteAllProcessInstancesClick = useCallback(() => {
        const customer = authentication?.customer;
        if (!customer) return;
        showDeleteDialog(
            t('processPanel.deleteAllProcessInstancesTitle'),
            customer,
            async () => {
                try {
                    const result = await userAPI.deleteAllProcessInstances(customer);
                    await loadBpmProcessInstances();
                    await loadOverallStatusInstances();
                    pushSuccess(
                        t('processPanel.deleteAllProcessInstancesSuccess', {
                            count: result?.purgedInstanceIds?.length ?? 0,
                        }),
                    );
                } catch (e) {
                    pushError(e);
                }
            },
        );
    }, [
        authentication?.customer,
        t,
        showDeleteDialog,
        loadBpmProcessInstances,
        loadOverallStatusInstances,
        pushError,
        pushSuccess,
    ]);

    const din276OptionsWithHierarchy = useCallback((metadata: any[]) => {
        const byCode = metadata.reduce((m: any, c: any) => { m[c.code] = c; return m; }, {});
        const getPath = (code: string): string[] => {
            const cat = byCode[code];
            if (!cat || (Number(cat.level) || 1) <= 1) return cat ? [code] : [];
            const level = Number(cat.level) || 1;
            const candidates = metadata.filter((c: any) => {
                const cLevel = Number(c.level) || 1;
                if (cLevel !== level - 1) return false;
                if (code.startsWith(c.code + '.')) return true;
                if (!c.code.includes('.') && !code.includes('.') && code !== c.code) {
                    return code.substring(0, c.code.length - 1) === c.code.substring(0, c.code.length - 1);
                }
                return false;
            });
            const parent = candidates.sort((a: any, b: any) => b.code.length - a.code.length)[0];
            return parent ? [...getPath(parent.code), code] : [code];
        };
        return metadata
            .sort((a: any, b: any) => {
                const ap = (a.code || '').split(/\./).map(Number);
                const bp = (b.code || '').split(/\./).map(Number);
                for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
                    const av = ap[i] ?? 0;
                    const bv = bp[i] ?? 0;
                    if (av !== bv) return av - bv;
                }
                return 0;
            })
            .map((cat: any) => {
                const path = getPath(cat.code);
                const pathNames = path.map((c: string) => byCode[c]?.name || c).join(' › ');
                const displayText = path.length > 1 ? `${cat.code} – ${pathNames}` : `${cat.code} (${cat.name || ''})`;
                return { key: cat.code, text: displayText, data: { cat, pathNames, searchText: `${cat.code} ${cat.name || ''} ${pathNames}`.toLowerCase() } };
            });
    }, []);

    const din276OptionsWithPath = useMemo(() => din276OptionsWithHierarchy(din276Metadata), [din276Metadata, din276OptionsWithHierarchy]);

    const din276FilteredOptions = useMemo(() => {
        if (!din276Filter.trim()) return din276OptionsWithPath;
        const q = din276Filter.trim().toLowerCase();
        return din276OptionsWithPath.filter((o: Din276Option) => o.data?.searchText?.includes(q));
    }, [din276OptionsWithPath, din276Filter]);
    const [state, _setState] = useState({
        options: {
            chart: {
                type: 'bar',
                height: 350,
                animations: {
                    enabled: true,
                    speed: 800,
                    animateGradually: {
                        enabled: true,
                        delay: 150
                    },
                    dynamicAnimation: {
                        enabled: true,
                        speed: 350
                    }
                }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '40%',
                    borderRadius: 0,
                    borderRadiusApplication: 'end'
                },
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            yaxis: {
                stepSize: 1,
                tickAmount: 5,
                title: {
                    text: t('processPanel.tasks')
                },
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    //formatter: (date: Date) => dayjs(date).format('DD.MM'),
                    datetimeFormatter: {
                        year: 'yyyy',
                        month: "MMM 'yy",
                        day: 'dd.MM',
                        hour: 'HH:mm',
                        minute: 'HH:mm:ss',
                        second: 'HH:mm:ss',
                    },
                },
            },
            fill: {
                opacity: 1
            },
            colors,
            tooltip: {
                y: {
                    formatter: function (val: any) {
                        return "" + val + " tasks"
                    }
                }
            }
        },
    });

    const pieOptions = useMemo(() => ({
        chart: {
            animations: {
                enabled: true,
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            },
        },
        legend: {
            show: true,
            position: 'bottom',
        }
    }), []);

    interface DraggableProperties {
        children?: any;
        item?: any;
        type: string
    }

    const Draggable = (properties: DraggableProperties) => {
        const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
            type: properties.type,
            item: properties.item,
            collect: (monitor) => ({
                isDragging: monitor.isDragging()
            })
        }))

        return <>
            <div ref={dragPreview} className={cn('w-full min-w-0', isDragging && 'opacity-50')}>
                <div ref={drag} className="w-full min-w-0">
                    {properties && properties.children
                        ?
                        (properties.children)
                        :
                        <></>
                    }
                </div>
            </div>
        </>;
    }

    const DropZone = (properties: DropZoneProperties) => {
        const onDropRef = useRef(properties.onDrop);
        onDropRef.current = properties.onDrop;
        const [{ isOver }, drop] = useDrop(
            () => ({
                accept: [ItemTypes.CARD, ItemTypes.TYPE],
                drop: (item: any) => {
                    onDropRef.current(item);
                },
                collect: (monitor) => ({
                    isOver: monitor.isOver({ shallow: true }),
                }),
            }),
            [],
        );

        // Compact layout: thin strip until drag-over (matches pre-refactor spacing).
        return (
            <div
                ref={drop}
                aria-hidden
                className={cn(
                    'w-full shrink-0 self-stretch rounded-sm transition-[min-height] duration-150',
                    isOver
                        ? 'my-2 min-h-[40px] border-2 border-[color:var(--customer-primary,hsl(var(--primary)))] bg-accent p-2'
                        : 'min-h-[5px] border-none',
                )}
            />
        );
    }

    const ChecklistFieldRow = (props: ChecklistFieldRowProps) => {
        const { field, fieldPath, showRootDropZone, selectedField, selectChecklistField, selectedChecklist, setSelectedChecklist, insertAtIndex, addToContainer, addAtIndex, removeField, renderFieldPreview, t } = props;
        const isContainer = isChecklistContainerField(field);
        const children = getChecklistFieldChildren(field);

        const handleDrop = (item: any, targetIndex: number) => {
            const ft = dragItemFieldType(item);
            const fromPalette = isChecklistPaletteDragItem(item);
            addAtIndex(
                fromPalette
                    ? {
                          type: ft ?? item.type,
                          text: t('processPanel.newField'),
                          ...defaultFieldsForPaletteType(ft ?? item.type ?? ''),
                          ...(CONTAINER_TYPES.includes(ft ?? '') ? { children: [] } : {}),
                          __checklistPalette: true,
                      }
                    : item,
                targetIndex
            );
        };

        const handleDropInContainer = (item: any, atIndex: number) => {
            if (!canDropInContainer(item, field)) {
                console.log('[checklist-def] drop rejected', {
                    path: fieldPath.join('.'),
                    containerKind: fieldKind(field),
                    dragKind: dragItemFieldType(item),
                });
                return;
            }
            const ft = dragItemFieldType(item);
            const fromPalette = isChecklistPaletteDragItem(item);
            console.log('[checklist-def] drop into container', {
                path: fieldPath.join('.'),
                atIndex,
                addKind: ft,
                fromPalette,
            });
            addToContainer(
                fieldPath,
                fromPalette
                    ? {
                          type: ft ?? item.type,
                          text: t('processPanel.newField'),
                          ...defaultFieldsForPaletteType(ft ?? item.type ?? ''),
                          ...(CONTAINER_TYPES.includes(ft ?? '') ? { children: [] } : {}),
                          __checklistPalette: true,
                      }
                    : item,
                atIndex
            );
        };

        const containerIsSelected = isContainer && field === selectedField;

        const updateContainerChildren = (nextChildren: any[]) => {
            setSelectedChecklist((prev: any) => {
                const fields = prev?.fields ?? [];
                const container = getFieldAtPath(fields, fieldPath);
                if (!container) return prev;
                const newFields = updateFieldInTree(fields, container, (f) => ({ ...f, children: nextChildren }));
                return { ...prev, fields: newFields };
            });
        };

        const ContainerChrome = isContainer
            ? ({ children: ch }: { children: React.ReactNode }) => (
                <div
                    role="presentation"
                    className={cn(
                        'mt-2 flex w-full min-w-0 flex-1 flex-col rounded-md px-4 py-2',
                        containerIsSelected ? 'border border-solid border-foreground/40 bg-accent' : 'border border-dashed border-border bg-card',
                    )}
                    onClick={() => selectChecklistField(field, fieldPath)}
                >
                    {ch}
                </div>
            )
            : ({ children: ch }: { children: React.ReactNode }) => <>{ch}</>;

        return (
            <div key={fieldPath.join('-') + field.type} className="w-full">
                <div className="flex w-full items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('processPanel.removeField', { field: field.text ?? '' })}
                        title={t('processPanel.removeField', { field: field.text ?? '' })}
                        onClick={() => removeField(field)}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                    <div className="w-full min-w-0 flex-1">
                        <ContainerChrome>
                            <Draggable type={ItemTypes.CARD} item={field}>
                                <div
                                    role="presentation"
                                    className={cn(
                                        'mt-2 flex w-full min-w-0 flex-wrap items-start gap-2',
                                        !isContainer
                                            ? (field === selectedField ? FIELD_ROW_SELECTED_CLASS : FIELD_ROW_CLASS)
                                            : 'm-0 cursor-move border-none bg-transparent p-0',
                                    )}
                                    onClick={(e: any) => { e.stopPropagation(); selectChecklistField(field, fieldPath); }}
                                >
                                    <div className="min-w-0 flex-1 break-words text-sm">{field.text}</div>
                                    <div className="flex w-[150px] shrink-0 items-start">{renderFieldPreview(field)}</div>
                                </div>
                            </Draggable>
                            {isContainer && (
                                <div className="mt-4 flex w-full flex-col">
                                    <DropZone onDrop={(item: any) => handleDropInContainer(item, 0)} />
                                    {children.map((child: any, ci: number) => (
                                        <React.Fragment key={`${fieldPath.join('-')}-${ci}`}>
                                            {isChecklistContainerField(child) ? (
                                                <ChecklistFieldRow
                                                    field={child}
                                                    fieldPath={[...fieldPath, ci]}
                                                    showRootDropZone={false}
                                                    selectedField={selectedField}
                                                    selectChecklistField={selectChecklistField}
                                                    selectedChecklist={selectedChecklist}
                                                    setSelectedChecklist={setSelectedChecklist}
                                                    insertAtIndex={insertAtIndex}
                                                    addToContainer={addToContainer}
                                                    addAtIndex={addAtIndex}
                                                    removeField={removeField}
                                                    renderFieldPreview={renderFieldPreview}
                                                    t={t}
                                                />
                                            ) : (
                                                <div className="flex w-full items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={t('processPanel.removeField', { field: child.text ?? '' })}
                                                        title={t('processPanel.removeField', { field: child.text ?? '' })}
                                                        onClick={() => {
                                                            const nextChildren = children.filter((_: any, i: number) => i !== ci);
                                                            updateContainerChildren(nextChildren);
                                                            if (child === selectedField) selectChecklistField(null, null);
                                                        }}
                                                    >
                                                        <Trash2 aria-hidden className="h-4 w-4" />
                                                    </Button>
                                                    <div className="min-w-0 flex-1">
                                                        <Draggable type={ItemTypes.CARD} item={child}>
                                                            <div
                                                                role="presentation"
                                                                className={cn(
                                                                    'mt-2 flex w-full min-w-0 flex-wrap items-start gap-2 rounded-md border border-dashed border-border px-3 py-1.5 cursor-move',
                                                                    child === selectedField ? 'bg-accent' : 'bg-card',
                                                                )}
                                                                onClick={(e: any) => { e.stopPropagation(); selectChecklistField(child, [...fieldPath, ci]); }}
                                                            >
                                                                <div className="min-w-0 flex-1 break-words text-sm">{child.text}</div>
                                                                <div className="flex w-[120px] shrink-0 items-start">{renderFieldPreview(child)}</div>
                                                            </div>
                                                        </Draggable>
                                                    </div>
                                                </div>
                                            )}
                                            <DropZone onDrop={(item: any) => handleDropInContainer(item, ci + 1)} />
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </ContainerChrome>
                    </div>
                </div>
                {showRootDropZone ? <DropZone onDrop={(item: any) => handleDrop(item, insertAtIndex)} /> : null}
            </div>
        );
    };

    const renderFieldPreview = useCallback((field: any) => {
        const containerCanon = canonicalContainerFieldType(fieldKind(field));
        if (containerCanon === 'loop' || containerCanon === 'group') {
            return <FieldTypeHint type={containerCanon} label={t(`processPanel.${containerCanon}`)} />;
        }
        switch (fieldKind(field) ?? field.type) {
            case 'text': return <Textarea rows={2} readOnly aria-hidden tabIndex={-1} />;
            case 'integer': return <Input readOnly aria-hidden tabIndex={-1} />;
            case 'decimal': return <Input readOnly aria-hidden tabIndex={-1} />;
            case 'OKNotOK': return (
                <div className="flex items-center gap-2">
                    <Switch checked={false} disabled aria-hidden tabIndex={-1} />
                    <span className="text-sm">{t('processPanel.notOk')}</span>
                </div>
            );
            case 'DoneNotDone': return (
                <div className="flex items-center gap-2">
                    <Switch checked={false} disabled aria-hidden tabIndex={-1} />
                    <span className="text-sm">{t('processPanel.notDone')}</span>
                </div>
            );
            case 'rating': return (
                <StarRating value={3} max={5} readOnly label={t('processPanel.ratingStars', { value: 3, max: 5 })} />
            );
            case 'photo': return <FieldTypeHint type="photo" label={t('processPanel.takePhoto')} />;
            case 'signature': return <FieldTypeHint type="signature" label={t('processPanel.signature')} />;
            case 'buildingCapture': return <FieldTypeHint type="buildingCapture" label={t('processPanel.buildingCapture')} />;
            case 'floorCapture': return <FieldTypeHint type="floorCapture" label={t('processPanel.floorCapture')} />;
            case 'roomCapture': return <FieldTypeHint type="roomCapture" label={t('processPanel.roomCapture')} />;
            case 'assetCapture': return <div className="flex min-w-0 flex-col gap-1">
                <FieldTypeHint type="assetCapture" label={t('processPanel.assetCapture')} />
                {field.schemaCategory && <div className="min-w-0 break-words text-sm text-muted-foreground">{din276OptionsWithPath.find((o: any) => o.key === field.schemaCategory)?.data?.pathNames || din276Metadata.find((c: any) => c.code === field.schemaCategory)?.name || field.schemaCategory}</div>}
            </div>;
            default: return null;
        }
    }, [t, din276OptionsWithPath, din276Metadata]);

    const findField = useCallback(
        (index: string) => {
            const fields = selectedChecklist?.fields ?? [];
            const field = fields.filter((f: any) => `${f.index}` === index)[0];

            return {
                field,
                index: fields.indexOf(field),
            }
        },
        [selectedChecklist],
    )

    const _moveField = useCallback(
        (id: string, atIndex: number) => {
            if (!selectedChecklist?.fields) return;
            const { field, index } = findField(id);
            if (index < 0 || !field) return;

            setSelectedChecklist({
                ...selectedChecklist, fields: update(selectedChecklist.fields, {
                    $splice: [
                        [index, 1],
                        [atIndex, 0, field],
                    ],
                })
            });
        },
        [findField, setSelectedChecklist, selectedChecklist],
    )

    const selectTask = (task: any) => {
        setSelectedTask(task);
        setTaskComment('');

        if (task) {
        }
    };

    /** Symbol der Aufgaben-/Prozessart samt Beschriftung für Screenreader. */
    const renderTaskTypeIcon = (type: string | undefined) => {
        switch (type) {
            case 'case': return <FileText aria-label={t('processPanel.typeCase')} className="h-5 w-5 text-[color:var(--customer-secondary-dark,hsl(var(--foreground)))]" />;
            case 'checklist': return <ListChecks aria-label={t('processPanel.checklist')} className="h-5 w-5 text-[color:var(--customer-tertiary-dark,hsl(var(--foreground)))]" />;
            case 'process': return <Settings aria-label={t('processPanel.process')} className="h-5 w-5 text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]" />;
            default: return null;
        }
    };

    const taskColumns: ColumnDef<any>[] = [{
        id: 'open',
        header: () => <span className="sr-only">{t('processPanel.editTask')}</span>,
        size: 56,
        enableSorting: false,
        cell: ({ row }) => (
            <Button
                variant="ghost"
                size="icon"
                title={t('processPanel.editTask')}
                aria-label={t('processPanel.editTask')}
                onClick={(event) => {
                    event.stopPropagation();
                    selectTask(row.original);
                }}
            >
                <Pencil aria-hidden className="h-4 w-4" />
            </Button>
        ),
    }, {
        id: 'type',
        accessorFn: (row: any) => (row?.checklistType === 'instance' ? 'checklist' : row?.definition?.type ?? ''),
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.type')}</SortableHeader>,
        size: 96,
        cell: ({ row }) => renderTaskTypeIcon(
            row.original?.checklistType === 'instance' ? 'checklist' : row.original?.definition?.type,
        ),
    }, {
        id: 'task',
        accessorFn: (row: any) => row?.name ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.task')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original?.name}</span>,
    }, {
        id: 'site',
        accessorFn: (row: any) => row?.data?.site?.name ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.branchBuildingArea')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => {
            const site = row.original?.data?.site;
            if (!site) return <span className="text-sm">-</span>;
            return <div className="min-w-0 text-sm">
                <div className="break-words">{site.name}</div>
                <div className="break-words">{site.address?.street} {site.address?.streetNumber}, {site.address?.postalCode} {site.address?.city}</div>
            </div>;
        },
    }, {
        id: 'user',
        accessorFn: (row: any) => taskUserLabel(row),
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.user')}</SortableHeader>,
        size: 220,
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{taskUserLabel(row.original) || '-'}</span>,
    }, {
        id: 'due',
        accessorFn: (row: any) => row?.due?.getTime?.() ?? 0,
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.due')}</SortableHeader>,
        size: 150,
        cell: ({ row }) => row.original?.due ? (
            <div className="text-sm">
                <div>{row.original.due.toLocaleDateString()}</div>
                <div>{row.original.due.toLocaleTimeString()}</div>
            </div>
        ) : null,
    }, {
        id: 'role',
        accessorFn: (row: any) => qualificationRoleName(t, row?.role) ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.responsibleRoleDepartment')}</SortableHeader>,
        size: 200,
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{qualificationRoleName(t, row.original?.role) ?? '-'}</span>,
    }];

    const combinedTasks = useMemo(() => [...(tasks ?? []), ...(checklistInstances ?? []).map((c: any) => ({ ...c, _rowType: 'checklist' }))], [tasks, checklistInstances]);

    const openChecklistDefinition = (process: any) => {
        const raw = resolveFieldsFromProcessOrChecklistRow(process);
        const fields = normalizeChecklistFieldTree(raw);
        console.log('[checklist-def] load row', { id: process?._id, name: process?.name, fieldCount: fields?.length });
        selectChecklistField(null, null);
        setSelectedChecklist({ ...process, fields });
    };

    const scheduleColumns: ColumnDef<any>[] = [{
        id: 'edit',
        header: () => <span className="sr-only">{t('processPanel.editChecklist')}</span>,
        size: 56,
        enableSorting: false,
        cell: ({ row }) => (
            <Button
                variant="ghost"
                size="icon"
                title={t('processPanel.editChecklist')}
                aria-label={t('processPanel.editChecklist')}
                onClick={(event) => {
                    event.stopPropagation();
                    openChecklistDefinition(row.original);
                }}
            >
                <Pencil aria-hidden className="h-4 w-4" />
            </Button>
        ),
    }, {
        id: 'status',
        header: plainHeader(t('processPanel.status')),
        size: 150,
        enableSorting: false,
        cell: () => (
            <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                {t('processPanel.deactivate')}
            </Button>
        ),
    }, {
        id: 'type',
        accessorFn: (process: any) => process.type ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.type')}</SortableHeader>,
        size: 96,
        cell: ({ row }) => renderTaskTypeIcon(row.original?.type),
    }, {
        id: 'process',
        accessorFn: (process: any) => process.name ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.process')}</SortableHeader>,
        size: 300,
        cell: ({ row }) => {
            const process = row.original;
            const originKey = process.origin === 'general' || process.origin === 'vdma' || process.origin === 'internal'
                ? process.origin
                : null;
            return <div className="min-w-0 text-sm">
                {originKey ? <div className={cn('break-words', CUSTOMER_ACCENT)}>{t(`processPanel.${originKey}`)}</div> : null}
                <div className="break-words">{process.name}</div>
            </div>;
        },
    }, {
        id: 'objects',
        header: plainHeader(t('processPanel.objects')),
        size: 200,
        enableSorting: false,
        cell: ({ row }) => <div className="min-w-0 text-sm">
            {(row.original.data || []).map((object: any) => <div key={object.name} className="break-words">{object.name}</div>)}
        </div>,
    }, {
        id: 'period',
        accessorFn: (process: any) => process.period ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.period')}</SortableHeader>,
        size: 150,
        cell: ({ row }) => <span className="block text-right text-sm">{row.original.period || '-'}</span>,
    }, {
        id: 'role',
        accessorFn: (process: any) => process.role?.name ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.responsibleRoleDepartment')}</SortableHeader>,
        size: 200,
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.role
            ? `${row.original.role.name}${row.original.role.scope ? ` (${row.original.role.scope})` : ''}`
            : '-'}</span>,
    }, {
        id: 'roleCheck',
        accessorFn: (process: any) => process.roleCheck?.name ?? '',
        header: ({ column }) => <SortableHeader column={column}>{t('processPanel.roleDepartmentReviewer')}</SortableHeader>,
        size: 200,
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.roleCheck
            ? `${row.original.roleCheck.name}${row.original.roleCheck.scope ? ` (${row.original.roleCheck.scope})` : ''}`
            : '-'}</span>,
    }];
    const bpmProcessInstanceColumns: ColumnDef<any>[] = useMemo(
        () => [
            {
                id: 'details',
                header: () => <span className="sr-only">{t('processPanel.processInstanceDetails')}</span>,
                size: 56,
                enableSorting: false,
                cell: ({ row }) => (
                    <Button
                        variant="ghost"
                        size="icon"
                        title={t('processPanel.processInstanceDetails')}
                        aria-label={t('processPanel.processInstanceDetails')}
                        onClick={(event) => {
                            event.stopPropagation();
                            openBpmProcessDetail(row.original);
                        }}
                    >
                        <ExternalLink aria-hidden className="h-4 w-4" />
                    </Button>
                ),
            },
            {
                id: 'chat',
                header: () => <span className="sr-only">{t('processPanel.openInChat')}</span>,
                size: 56,
                enableSorting: false,
                cell: ({ row }) => {
                    const chatConvId =
                        row.original?.conversationId ?? row.original?.conversation?._id ?? row.original?.conversation?.id ?? null;
                    if (chatConvId == null || String(chatConvId) === '') {
                        return null;
                    }
                    return (
                        <Button
                            variant="ghost"
                            size="icon"
                            title={t('processPanel.openInChat')}
                            aria-label={t('processPanel.openInChat')}
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/chatbot?conversationId=${encodeURIComponent(String(chatConvId))}`);
                            }}
                        >
                            <MessageSquare aria-hidden className="h-4 w-4" />
                        </Button>
                    );
                },
            },
            ...(roleAccess.canDeleteProcessInstances
                ? [
                      {
                          id: 'delete',
                          header: () => <span className="sr-only">{t('processPanel.deleteProcessInstance')}</span>,
                          size: 56,
                          enableSorting: false,
                          cell: ({ row }: any) => (
                              <Button
                                  variant="ghost"
                                  size="icon"
                                  title={t('processPanel.deleteProcessInstance')}
                                  aria-label={t('processPanel.deleteProcessInstance')}
                                  onClick={(event: React.MouseEvent) => {
                                      event.stopPropagation();
                                      handleDeleteProcessInstanceClick(row.original);
                                  }}
                              >
                                  <Trash2 aria-hidden className="h-4 w-4" />
                              </Button>
                          ),
                      } as ColumnDef<any>,
                  ]
                : []),
            {
                id: 'id',
                accessorFn: (row: any) => String(row?._id ?? row?.id ?? ''),
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processInstanceId')}</SortableHeader>,
                size: 280,
                cell: ({ row }) => (
                    <span className="break-all font-mono text-xs">{row.original?._id ?? row.original?.id ?? '–'}</span>
                ),
            },
            {
                id: 'status',
                accessorFn: (row: any) => row?.status ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.status')}</SortableHeader>,
                size: 120,
                cell: ({ row }) => (
                    <span className="text-sm">
                        {row.original?.status != null && row.original.status !== ''
                            ? t(`processPanel.processInstanceState.${String(row.original.status)}`, {
                                  defaultValue: String(row.original.status),
                              })
                            : '–'}
                    </span>
                ),
            },
            {
                id: 'definition',
                accessorFn: (row: any) => resolveProcessDefinitionLabel(row),
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processInstanceDefinition')}</SortableHeader>,
                size: 260,
                cell: ({ row }) => <span className="min-w-0 break-words text-sm">{resolveProcessDefinitionLabel(row.original)}</span>,
            },
            {
                id: 'created',
                accessorFn: (row: any) => row?.createdAt ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processInstanceCreated')}</SortableHeader>,
                size: 180,
                cell: ({ row }) => <span className="whitespace-nowrap text-sm">{formatProcessTimestamp(row.original?.createdAt)}</span>,
            },
            {
                id: 'synopsis',
                accessorFn: (row: any) => row?.instanceSynopsis ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.instanceSynopsis')}</SortableHeader>,
                cell: ({ row }) => (
                    <span
                        className="block min-w-0 truncate text-sm"
                        title={row.original?.instanceSynopsis ?? ''}
                    >
                        {row.original?.instanceSynopsis ?? '–'}
                    </span>
                ),
            },
        ],
        [t, navigate, openBpmProcessDetail, roleAccess.canDeleteProcessInstances, handleDeleteProcessInstanceClick],
    );

    /**
     * Flattened process history with child histories inlined under the row
     * that triggered each sub-process. Expand/collapse via the chevron in
     * the leading column; depth controls indentation in the node-name cell.
     */
    const historyRowsFlat = useMemo(() => {
        if (!selectedBpmProcessDetail) return [];
        const parentHistory = normalizeProcessHistory(selectedBpmProcessDetail);
        const childByNodeId = buildChildDetailByNodeId(
            (selectedBpmProcessDetail as any)?.children ?? [],
        );
        return flattenHistoryWithChildren(parentHistory, childByNodeId, detailExpandedRowIds);
    }, [selectedBpmProcessDetail, detailExpandedRowIds]);

    const bpmHumanTaskColumns: ColumnDef<any>[] = useMemo(
        () => [
            {
                id: 'chat',
                header: () => <span className="sr-only">{t('processPanel.openInChat')}</span>,
                size: 56,
                enableSorting: false,
                cell: ({ row }) => {
                    const chatId = bpmProcessDetailChatConversationId;
                    const eligible = chatId && viewerMayExecuteHumanTask(authentication, row.original);
                    if (!eligible) {
                        return null;
                    }
                    return (
                        <Button
                            variant="ghost"
                            size="icon"
                            title={t('processPanel.openInChat')}
                            aria-label={t('processPanel.openInChat')}
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/chatbot?conversationId=${encodeURIComponent(String(chatId))}`);
                            }}
                        >
                            <Rocket aria-hidden className="h-4 w-4" />
                        </Button>
                    );
                },
            },
            {
                id: 'id',
                accessorFn: (r: any) => String(r?._id ?? r?.id ?? ''),
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processInstanceId')}</SortableHeader>,
                size: 200,
                cell: ({ row }) => (
                    <span className="break-all font-mono text-xs">{row.original?._id ?? row.original?.id ?? '–'}</span>
                ),
            },
            {
                id: 'status',
                accessorFn: (r: any) => r?.status ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.status')}</SortableHeader>,
                size: 120,
                cell: ({ row }) => {
                    const st = row.original?.status;
                    if (st == null || st === '') return <span className="text-sm">–</span>;
                    const s = String(st);
                    return (
                        <span className="text-sm">
                            {t(`processPanel.processHumanTaskState.${s}`, { defaultValue: s })}
                        </span>
                    );
                },
            },
            {
                id: 'name',
                accessorFn: (r: any) => r?.name ?? r?.title ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.name')}</SortableHeader>,
                size: 200,
                cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original?.name ?? row.original?.title ?? ''}</span>,
            },
            {
                id: 'role',
                accessorFn: (r: any) => (typeof r?.role === 'string' ? r.role : r?.role?.name) ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHumanTaskRole')}</SortableHeader>,
                size: 180,
                cell: ({ row }) => (
                    <span className="min-w-0 break-words text-sm">
                        {(typeof row.original?.role === 'string' ? row.original.role : row.original?.role?.name) ?? '–'}
                    </span>
                ),
            },
            {
                id: 'created',
                accessorFn: (r: any) => r?.createdAt ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.created')}</SortableHeader>,
                size: 170,
                cell: ({ row }) => <span className="whitespace-nowrap text-sm">{formatProcessTimestamp(row.original?.createdAt)}</span>,
            },
            {
                id: 'synopsis',
                accessorFn: (r: any) => r?.instanceSynopsis ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.instanceSynopsis')}</SortableHeader>,
                cell: ({ row }) => (
                    <span className="block min-w-0 truncate text-sm" title={row.original?.instanceSynopsis ?? ''}>
                        {row.original?.instanceSynopsis ?? '–'}
                    </span>
                ),
            },
        ],
        [t, navigate, authentication, bpmProcessDetailChatConversationId],
    );

    const bpmProcessHistoryColumns: ColumnDef<any>[] = useMemo(
        () => [
            {
                id: 'expand',
                header: () => <span className="sr-only">{t('ui.expand')}</span>,
                size: 48,
                enableSorting: false,
                cell: ({ row }) => {
                    const hasChildren = !!row.original?._hasChildHistory;
                    const rowKey = String(row.original?._rowKey ?? '');
                    const isExpanded = detailExpandedRowIds.has(rowKey);
                    if (!hasChildren) {
                        return <span aria-hidden className="inline-block h-8 w-8" />;
                    }
                    return (
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-expanded={isExpanded}
                            title={isExpanded ? t('ui.collapse') : t('ui.expand')}
                            aria-label={isExpanded ? t('ui.collapse') : t('ui.expand')}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleDetailRowExpanded(rowKey);
                            }}
                        >
                            {isExpanded
                                ? <ChevronDown aria-hidden className="h-4 w-4" />
                                : <ChevronRight aria-hidden className="h-4 w-4" />}
                        </Button>
                    );
                },
            },
            {
                id: 'node',
                accessorFn: (r: any) => r?.nodeName ?? r?.nodeId ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHistoryNode')}</SortableHeader>,
                size: 320,
                cell: ({ row }) => (
                    <span
                        className="block min-w-0 break-words text-sm"
                        style={{ paddingLeft: ((row.original?._depth as number) ?? 0) * 16 }}
                    >
                        {row.original?.nodeName ?? row.original?.nodeId ?? '–'}
                    </span>
                ),
            },
            {
                id: 'event',
                accessorFn: (r: any) => r?.eventType ?? r?.type ?? r?.event ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHistoryEvent')}</SortableHeader>,
                size: 160,
                cell: ({ row }) => {
                    const ev = row.original?.eventType ?? row.original?.type ?? row.original?.event;
                    return (
                        <span className="min-w-0 break-words text-sm">
                            {ev != null && ev !== ''
                                ? t(`processPanel.processEventType.${String(ev)}`, { defaultValue: String(ev) })
                                : '–'}
                        </span>
                    );
                },
            },
            {
                id: 'when',
                accessorFn: (r: any) => r?.at ?? r?.timestamp ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHistoryWhen')}</SortableHeader>,
                size: 170,
                cell: ({ row }) => (
                    <span className="whitespace-nowrap text-sm">{formatProcessTimestamp(row.original?.at ?? row.original?.timestamp)}</span>
                ),
            },
            {
                id: 'nodeType',
                accessorFn: (r: any) => r?.nodeType ?? '',
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHistoryNodeType')}</SortableHeader>,
                size: 130,
                cell: ({ row }) => {
                    const nt = row.original?.nodeType;
                    if (nt == null || nt === '') return <span className="text-sm">–</span>;
                    const s = String(nt);
                    return (
                        <span className="min-w-0 break-words text-sm">
                            {t(`processPanel.processHistoryNodeTypeKind.${s}`, { defaultValue: s })}
                        </span>
                    );
                },
            },
            {
                id: 'performedBy',
                accessorFn: (r: any) => formatProcessHistoryActor(r),
                header: ({ column }) => <SortableHeader column={column}>{t('processPanel.processHistoryPerformedBy')}</SortableHeader>,
                size: 200,
                cell: ({ row }) => {
                    const label = formatProcessHistoryActor(row.original);
                    return <span className="min-w-0 break-words text-sm">{label !== '' ? label : '–'}</span>;
                },
            },
            {
                id: 'checklist',
                header: plainHeader(t('processPanel.checklistColumn')),
                size: 120,
                enableSorting: false,
                cell: ({ row }) => {
                    const wid = normalizeBpmWorkItemId(row.original?.workItemId);
                    const ev = String(row.original?.eventType ?? row.original?.type ?? row.original?.event ?? '');
                    if (wid == null || ev !== 'TASK_COMPLETED') return <span className="text-sm">–</span>;
                    const inst = bpmInstanceChecklistsByWorkItem[wid];
                    if (!inst) return <span className="text-sm">–</span>;
                    return (
                        <Button
                            variant="ghost"
                            size="icon"
                            title={t('processPanel.openChecklist')}
                            aria-label={t('processPanel.openChecklist')}
                            onClick={(event) => {
                                event.stopPropagation();
                                setViewingChecklistInstance(inst);
                            }}
                        >
                            <ListChecks aria-hidden className="h-4 w-4" />
                        </Button>
                    );
                },
            },
        ],
        [t, detailExpandedRowIds, toggleDetailRowExpanded, bpmInstanceChecklistsByWorkItem],
    );

    useEffect(() => {
        const call = async () => {
            try {
                const newRoles = await userAPI.query(new Query(new EntityQuery(Role.type, [], undefined,
                    or(is('type').equal(RoleType.role), is('type').equal(RoleType.parameterizableRole)))));

                setRoles(newRoles);
            } catch (error) {
                pushError(error);
            }
        };

        call();
    }, [pushError]);

    /** Law-derived masters store the STABLE role id (LEGAL-12); the editor
     *  dropdowns are keyed by role name — resolve an id to its display name
     *  so the current selection renders, while free-text names pass through. */
    const roleSelectionKey = useCallback((value: any) => {
        if (!value) return value;
        const match = roles.find((role: any) => role.id === value);
        return match ? match.name : value;
    }, [roles]);

    useEffect(() => {
        // Ohne Mandant gibt es nichts zu laden — dann auch keinen Ladezustand
        // stehen lassen, sonst zeigt die Aufgabentabelle dauerhaft Skeletons.
        if (!authentication?.customer) {
            setTasksLoading(false);
            return;
        }

        const call = async () => {
            try {
                const newProcessDefinitions = await userAPI.getProcessDefinitions(authentication.customer);

                setProcessDefinitions(newProcessDefinitions ?? []);

                const newTasks = await userAPI.getOpenTasks(authentication.customer);

                setTasks(newTasks ?? []);

                const newChecklistInstances = await userAPI.getChecklistInstances(authentication.customer);

                setChecklistInstances(newChecklistInstances ?? []);
            } catch (error) {
                pushError(error);
            } finally {
                setChecklistInstancesLoaded(true);
                setTasksLoading(false);
            }
        };

        call();
    }, [selectedTask, authentication?.customer, checklistRefreshTrigger, pushError]);

    const isChecklistComplete = (checklist: Checklist) => {
        return checklist.name && checklist.name.trim().length > 0
            && checklist.role;
    }

    const processInstanceTotalCount = overallStatusInstances.length;
    const processInstanceRunningCount = useMemo(
        () =>
            overallStatusInstances.filter(
                (inst) => String(inst?.status ?? '').toUpperCase() === 'RUNNING',
            ).length,
        [overallStatusInstances],
    );

    const processInstanceDefinitionPie = useMemo(() => {
        const byDef = aggregateProcessInstancesByDefinition(overallStatusInstances);
        const entries = Object.entries(byDef).sort((a, b) => b[1] - a[1]);
        if (entries.length === 0) {
            return { labels: ['–'], series: [0] };
        }
        return {
            labels: entries.map(([k]) => k),
            series: entries.map(([, v]) => v),
        };
    }, [overallStatusInstances]);

    useEffect(() => {
        setPieHoveredDefinitionLabel(null);
    }, [overallStatusInstances]);

    useEffect(() => {
        if (tab !== 'overallStatus') setPieHoveredDefinitionLabel(null);
    }, [tab]);

    const instancesForTimelineChart = useMemo(() => {
        if (pieHoveredDefinitionLabel == null) return overallStatusInstances;
        return overallStatusInstances.filter(
            (inst) => resolveProcessDefinitionLabel(inst) === pieHoveredDefinitionLabel,
        );
    }, [overallStatusInstances, pieHoveredDefinitionLabel]);

    const processInstanceTimelineBuckets = useMemo(
        () => buildProcessInstanceTimelineBuckets(instancesForTimelineChart, processInstanceTimelineInterval),
        [instancesForTimelineChart, processInstanceTimelineInterval],
    );

    const processDefinitionPieChartOptions = useMemo(
        () =>
            ({
                ...pieOptions,
                colors,
                labels: processInstanceDefinitionPie.labels,
                chart: {
                    ...pieOptions.chart,
                    events: {
                        dataPointMouseEnter: (_e: unknown, _ctx: unknown, config: { dataPointIndex?: number }) => {
                            const idx = config?.dataPointIndex;
                            if (idx == null || idx < 0) return;
                            const label = processInstanceDefinitionPie.labels[idx];
                            if (label != null) setPieHoveredDefinitionLabel(String(label));
                        },
                        dataPointMouseLeave: () => {
                            setPieHoveredDefinitionLabel(null);
                        },
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val: number) => `${val} ${t('processPanel.instancesShort')}`,
                    },
                },
            }) as any,
        [processInstanceDefinitionPie.labels, colors, pieOptions, t],
    );

    const processInstanceTimelineChartOptions = useMemo(() => {
        const instWord = t('processPanel.instancesShort');
        // Tick column width: smaller = digits sit closer to the HTML axis title (title is outside chart).
        const yAxisTickColumnPx = 44;
        const barGrey = '#c8c6c4';
        let barColor = barGrey;
        if (pieHoveredDefinitionLabel != null) {
            const idx = processInstanceDefinitionPie.labels.findIndex((l) => String(l) === pieHoveredDefinitionLabel);
            if (idx >= 0) {
                barColor = colors[idx % colors.length];
            }
        }
        return {
            ...state.options,
            chart: {
                ...state.options.chart,
                type: 'bar',
                height: PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT,
                offsetX: -3,
            },
            grid: {
                ...((state.options as { grid?: Record<string, unknown> }).grid ?? {}),
                padding: {
                    ...((state.options as { grid?: { padding?: Record<string, unknown> } }).grid?.padding ?? {}),
                    left: 2,
                    right: 8,
                    top: 4,
                    bottom: 2,
                },
            },
            xaxis: {
                ...state.options.xaxis,
                type: 'category',
                categories: processInstanceTimelineBuckets.categories,
                ...(processInstanceTimelineInterval === 'today'
                    ? {
                          labels: {
                              rotate: -45,
                              rotateAlways: true,
                              hideOverlappingLabels: false,
                              style: { fontSize: '10px' },
                          },
                      }
                    : {}),
            },
            yaxis: {
                ...state.options.yaxis,
                // Title is rendered as HTML beside the chart so it does not move when tick digits change.
                title: {
                    text: '',
                },
                labels: {
                    minWidth: yAxisTickColumnPx,
                    maxWidth: yAxisTickColumnPx,
                    align: 'right',
                    offsetX: -10,
                    style: {
                        fontSize: '11px',
                    },
                },
            },
            tooltip: {
                y: {
                    formatter: (val: number) => `${val} ${instWord}`,
                },
            },
            colors: [barColor],
        };
    }, [
        state.options,
        processInstanceTimelineBuckets.categories,
        processInstanceTimelineInterval,
        t,
        pieHoveredDefinitionLabel,
        processInstanceDefinitionPie.labels,
        colors,
    ]);
    /** Statusbezeichnung + Badge-Variante eines Startereignisses. */
    const scheduleStatusBadge = (status: string): { label: string; variant: 'success' | 'warning' | 'secondary' | 'destructive' } => {
        switch (status) {
            case 'active': return { label: t('processPanel.scheduleStatusActive'), variant: 'success' };
            case 'paused': return { label: t('processPanel.scheduleStatusPaused'), variant: 'warning' };
            case 'exhausted': return { label: t('processPanel.scheduleStatusExhausted'), variant: 'secondary' };
            default: return { label: t('processPanel.scheduleStatusNotDeployed'), variant: 'destructive' };
        }
    };

    const renderEventSchedules = () => (
        <div className="flex min-w-0 flex-col gap-3">
            <div className="flex flex-wrap justify-end gap-2">
                <Button variant="ghost" className="gap-2" onClick={loadSchedules} disabled={schedulesLoading}>
                    <RefreshCw aria-hidden className={cn('h-4 w-4', schedulesLoading && 'animate-spin')} />
                    {schedulesLoading ? t('ui.loading') : t('ui.refresh')}
                </Button>
            </div>
            {schedulesLoading && bpmSchedules.length === 0 && (
                <div className="flex min-w-0 flex-col gap-3" aria-busy="true" aria-label={t('ui.loading')}>
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                </div>
            )}
            {bpmSchedules.length === 0 && !schedulesLoading && (
                <PanelEmptyState
                    title={t('processPanel.scheduleNoData')}
                    hint={t('processPanel.scheduleNoDataHint')}
                    action={
                        <Button variant="outline" size="sm" className="gap-2" onClick={loadSchedules}>
                            <RefreshCw aria-hidden className="h-4 w-4" />
                            {t('ui.refresh')}
                        </Button>
                    }
                />
            )}
            {bpmSchedules.map((wf: any) => {
                const events: any[] = wf.startEvents ?? [];
                const hasAnyActive = events.some((e: any) => (e.status ?? '').toLowerCase() === 'active');
                const allNotDeployed = events.every((e: any) => (e.status ?? '').toLowerCase() === 'not_deployed');
                const allPausedOrNotDeployed = events.every((e: any) => {
                    const s = (e.status ?? '').toLowerCase();
                    return s === 'paused' || s === 'not_deployed';
                });

                const workflowStatus = (() => {
                    if (allNotDeployed) return scheduleStatusBadge('not_deployed');
                    if (hasAnyActive) return scheduleStatusBadge('active');
                    return scheduleStatusBadge('paused');
                })();
                const busy = scheduleToggling === wf.workflowId;

                return (
                    <div key={wf.workflowId} className="min-w-0 rounded-md border p-4">
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                            <div className="flex gap-1">
                                {(allPausedOrNotDeployed || allNotDeployed) && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={busy}
                                        title={t('processPanel.scheduleActivate')}
                                        aria-label={t('processPanel.scheduleActivate')}
                                        onClick={async () => {
                                            setScheduleToggling(wf.workflowId);
                                            try {
                                                await userAPI.activateBpmSchedule(authentication.customer, wf.workflowId);
                                                await new Promise(r => setTimeout(r, 800));
                                                pushSuccess(t('processPanel.scheduleActivatedSuccess'));
                                            } catch (e) { console.error('Activate failed:', e); }
                                            setScheduleToggling(null);
                                            loadSchedules();
                                        }}
                                    >
                                        {busy
                                            ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                            : <Play aria-hidden className="h-4 w-4 text-success" />}
                                    </Button>
                                )}
                                {hasAnyActive && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        disabled={busy}
                                        title={t('processPanel.scheduleDeactivate')}
                                        aria-label={t('processPanel.scheduleDeactivate')}
                                        onClick={async () => {
                                            setScheduleToggling(wf.workflowId);
                                            try {
                                                await userAPI.deactivateBpmSchedule(authentication.customer, wf.workflowId);
                                                pushSuccess(t('processPanel.scheduleDeactivatedSuccess'));
                                            } catch (e) { console.error('Deactivate failed:', e); }
                                            setScheduleToggling(null);
                                            loadSchedules();
                                        }}
                                    >
                                        {busy
                                            ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                            : <Pause aria-hidden className="h-4 w-4 text-warning" />}
                                    </Button>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="min-w-0 break-words text-sm font-semibold">{wf.workflowName}</div>
                            </div>
                            {wf.workflowId && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    title={t('processPanel.scheduleOpenWorkflow')}
                                    aria-label={t('processPanel.scheduleOpenWorkflow')}
                                    onClick={() => navigate(`/settings?panel=agent&tab=4&key=workflow&agenticWorkflowId=${wf.workflowId}&marketplacePage=1&agenticWorkflowPage=1`)}
                                >
                                    <ExternalLink aria-hidden className="h-4 w-4" />
                                </Button>
                            )}
                            <Badge variant={workflowStatus.variant}>{workflowStatus.label}</Badge>
                        </div>

                        <div className="mt-3 flex min-w-0 flex-col gap-1.5">
                            {events.map((ev: any, idx: number) => {
                                const s = (ev.status ?? '').toLowerCase();
                                const eventStatus = scheduleStatusBadge(s);
                                const isTimer = ev.type === 'timer';
                                const EventIcon = isTimer
                                    ? Clock
                                    : ev.connectorType === 'rss'
                                        ? Rss
                                        : ev.connectorType === 'sharepoint-folder'
                                            ? Folder
                                            : Mail;
                                const scheduleId = ev.scheduleId as string | null;
                                const expanded = scheduleId ? expandedSchedules[scheduleId] === true : false;
                                const historyState = scheduleId ? fireHistory[scheduleId] : undefined;

                                return (
                                    <div key={ev.nodeId ?? idx} className="min-w-0">
                                        <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-sm">
                                            {scheduleId ? (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="shrink-0"
                                                    aria-expanded={expanded}
                                                    title={expanded ? t('processPanel.scheduleCollapseHistory') : t('processPanel.scheduleExpandHistory')}
                                                    aria-label={expanded ? t('processPanel.scheduleCollapseHistory') : t('processPanel.scheduleExpandHistory')}
                                                    onClick={() => toggleScheduleExpansion(scheduleId)}
                                                >
                                                    {expanded
                                                        ? <ChevronDown aria-hidden className="h-4 w-4" />
                                                        : <ChevronRight aria-hidden className="h-4 w-4" />}
                                                </Button>
                                            ) : (
                                                <div aria-hidden className="w-8 shrink-0" />
                                            )}
                                            <EventIcon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                                            <span className="min-w-[60px] font-medium">
                                                {isTimer ? t('processPanel.scheduleTypTimer') : t('processPanel.scheduleTypeConnector')}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate font-mono text-xs">
                                                {isTimer
                                                    ? (ev.expression ?? '–')
                                                    : `${ev.connectorType ?? ''} — ${
                                                        ev.connectorType === 'rss'
                                                            ? (ev.config?.feedUrl ?? '–')
                                                            : ev.connectorType === 'sharepoint-folder'
                                                            ? (ev.config?.siteUrl ?? ev.config?.folderPath ?? '–')
                                                            : (ev.config?.mailbox ?? '–')
                                                    }`
                                                }
                                            </span>
                                            <Badge variant={eventStatus.variant} className="shrink-0">{eventStatus.label}</Badge>
                                            {ev.lastFired && (
                                                <span className="shrink-0 text-xs text-muted-foreground" title={dayjs(ev.lastFired).format('DD.MM.YYYY HH:mm')}>
                                                    {t('processPanel.scheduleLastFiredShort')} {dayjs(ev.lastFired).fromNow()}
                                                </span>
                                            )}
                                            {ev.nextFire && (
                                                <span className="shrink-0 text-xs text-muted-foreground" title={dayjs(ev.nextFire).format('DD.MM.YYYY HH:mm')}>
                                                    {t('processPanel.scheduleNextFireShort')} {dayjs(ev.nextFire).fromNow()}
                                                </span>
                                            )}
                                        </div>
                                        {expanded && scheduleId && (
                                            <div className="ml-8 mt-1 min-w-0 rounded-md bg-accent p-2 text-sm">
                                                {historyState?.loading && (
                                                    <Skeleton className="h-12 w-full" />
                                                )}
                                                {historyState?.events === null && (
                                                    <div role="alert" className="text-sm text-destructive">{t('processPanel.scheduleHistoryFailed')}</div>
                                                )}
                                                {Array.isArray(historyState?.events) && historyState!.events!.length === 0 && (
                                                    <div className="text-sm text-muted-foreground">{t('processPanel.scheduleHistoryEmpty')}</div>
                                                )}
                                                {Array.isArray(historyState?.events) && historyState!.events!.length > 0 && (
                                                    <div className="min-w-0 overflow-x-auto">
                                                        <Table>
                                                            <TableHeader>
                                                                <TableRow>
                                                                    <TableHead>{t('processPanel.scheduleHistoryFiredAt')}</TableHead>
                                                                    <TableHead>{t('processPanel.scheduleHistoryOutcome')}</TableHead>
                                                                    <TableHead>{t('processPanel.scheduleHistoryDuration')}</TableHead>
                                                                    <TableHead>{t('processPanel.scheduleHistoryCounts')}</TableHead>
                                                                    <TableHead>{t('processPanel.scheduleHistoryDetail')}</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {historyState!.events!.map((fe: any) => {
                                                                    const dropParts = Object.entries(fe.dropReasons ?? {})
                                                                        .map(([k, v]) => `${k}: ${v}`)
                                                                        .join(', ');
                                                                    return (
                                                                        <TableRow key={fe._id}>
                                                                            <TableCell className="whitespace-nowrap" title={dayjs(fe.firedAt).format('DD.MM.YYYY HH:mm:ss')}>
                                                                                {dayjs(fe.firedAt).format('DD.MM HH:mm:ss')}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <span className={cn(
                                                                                    'font-semibold',
                                                                                    fe.outcome === 'ok' ? 'text-success' : fe.outcome === 'error' ? 'text-destructive' : 'text-muted-foreground',
                                                                                )}>
                                                                                    {fe.outcome}
                                                                                </span>
                                                                            </TableCell>
                                                                            <TableCell className="whitespace-nowrap text-muted-foreground">{fe.durationMs}ms</TableCell>
                                                                            <TableCell
                                                                                className="whitespace-nowrap font-mono text-xs text-muted-foreground"
                                                                                title={t('processPanel.scheduleHistoryCountsHint', {
                                                                                    observed: fe.itemsObserved,
                                                                                    fired: fe.itemsFired,
                                                                                    skipped: fe.itemsSkipped,
                                                                                })}
                                                                            >
                                                                                {fe.itemsObserved}/{fe.itemsFired}/{fe.itemsSkipped}
                                                                            </TableCell>
                                                                            <TableCell className="min-w-0 break-words text-xs">
                                                                                {fe.error ? (
                                                                                    <span className="text-destructive" title={`${fe.error.stage}: ${fe.error.message}`}>
                                                                                        <strong>{fe.error.stage}:</strong> {fe.error.message}
                                                                                    </span>
                                                                                ) : dropParts ? (
                                                                                    <span className="text-muted-foreground" title={dropParts}>{dropParts}</span>
                                                                                ) : (
                                                                                    <span className="text-muted-foreground">–</span>
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
    const renderMainContent = () => {
        if (selectedTask) {
            const site = selectedTask.data?.site;
            const asset = selectedTask.data?.asset;

            return <div className="flex min-w-0 flex-col gap-4">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
                    <div className="flex min-w-0 flex-1 flex-col gap-3">
                        <h2 className="min-w-0 break-words text-lg font-semibold">{selectedTask.name}</h2>
                        {selectedTask.checklistType !== 'instance' && <div className="flex min-w-0 flex-wrap gap-6">
                            <div className="flex min-w-0 items-center gap-3">
                                <MapPin aria-hidden className="h-10 w-10 shrink-0 text-[color:var(--customer-secondary-dark,hsl(var(--foreground)))]" />
                                <div className="min-w-0">
                                    {site
                                        ?
                                        <>
                                            <div className="break-words font-bold">{site.name}</div>
                                            <div className="break-words text-sm">{site.address.street} {site.address.streetNumber}, {site.address.postalCode} {site.address.city}</div>
                                        </>
                                        :
                                        <></>}
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    title={t('processPanel.openBuilding')}
                                    aria-label={t('processPanel.openBuilding')}
                                    onClick={() => {
                                        const buildingId = selectedTask?.data?.site?.building?._id;
                                        if (buildingId) navigate(`/entity?selectedEntityType=Building&selectedEntity=${buildingId}`);
                                    }}
                                >
                                    <ExternalLink aria-hidden className="h-4 w-4" />
                                </Button>
                            </div>
                            {asset
                                ?
                                <div className="flex min-w-0 items-center gap-3">
                                    <HardDrive aria-hidden className="h-10 w-10 shrink-0 text-[color:var(--customer-tertiary-dark,hsl(var(--foreground)))]" />
                                    <div className="min-w-0">
                                        <div className="break-words font-bold">{asset.id}</div>
                                        <div className="break-words text-sm">{asset.schemaCategory} {asset.categoryName}</div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        title={t('processPanel.openAsset')}
                                        aria-label={t('processPanel.openAsset')}
                                        onClick={() => {
                                            navigate(`/entity?selectedEntityType=Asset&selectedEntity=${asset._id}`);
                                        }}
                                    >
                                        <ExternalLink aria-hidden className="h-4 w-4" />
                                    </Button>
                                </div>
                                :
                                <></>
                            }
                        </div>}
                        {selectedTask.checklistType === 'instance' && (
                            <div className="text-sm">
                                {t('processPanel.started')}: {selectedTask.startTime ? dayjs(selectedTask.startTime).format('DD.MM.YYYY HH:mm') : '-'}
                            </div>
                        )}
                        {selectedTask.checklistType === 'instance' && (selectedTask.asset || selectedTask.assetName) ? (
                            <ChecklistAssetContext
                                assetId={selectedTask.asset}
                                assetName={selectedTask.assetName}
                                floorId={selectedTask.floor}
                            />
                        ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2 max-sm:w-full">
                        <Button variant="outline" className="max-sm:w-full" onClick={async () => {
                            if (selectedTask.checklistType === 'instance') {
                                setSelectedTask(null);
                            } else {
                                await userAPI.deactivateTask(authentication.customer, selectedTask);
                                setSelectedTask(null);
                            }
                        }}>
                            {t('global.close')}
                        </Button>
                        {selectedTask.checklistType !== 'instance' && <>
                            <Button variant="outline" className="max-sm:w-full" onClick={async () => {
                                await userAPI.deactivateAndSaveTask(authentication.customer, selectedTask);
                                pushSuccess(t('processPanel.taskSavedSuccess'));
                                setSelectedTask(null);
                            }}>
                                {t('processPanel.saveAndClose')}
                            </Button>
                            <Button className="max-sm:w-full" onClick={async () => {
                                if (false) {
                                    setSignatureDialogOpen(true);
                                } else {
                                    await userAPI.completeTask(authentication.customer, selectedTask, taskComment);
                                    pushSuccess(t('processPanel.taskCompletedSuccess'));
                                    setTaskComment('');
                                    setSelectedTask(null);
                                }
                            }}>
                                {t('processPanel.complete')}
                            </Button>
                        </>}
                    </div>
                </div>
                <div className="flex min-w-0 flex-wrap gap-4">
                    <div className="flex min-w-[280px] flex-1 flex-col gap-4">
                        {selectedTask.checklistType !== 'instance' && <Section title={t('processPanel.history')}>
                            <div className="min-w-0 overflow-x-auto">
                                <div className="flex min-w-0 flex-wrap gap-2 text-sm">
                                    <div className="w-[70px] text-right">{(selectedTask.created ?? selectedTask.startTime)?.toLocaleDateString?.() ?? ''}</div>
                                    <div className="w-[100px] text-right">{(selectedTask.created ?? selectedTask.startTime)?.toLocaleTimeString?.() ?? ''}</div>
                                    <div className="w-[150px] break-words">{taskUserLabel(selectedTask) || null}</div>
                                </div>
                                {(selectedTask.history || []).filter((item: any) => item.action === 'comment').map((item: any, index: number) => <div key={index} className="mt-1 flex min-w-0 flex-wrap gap-2 text-sm">
                                    <div className="w-[70px] text-right">{item.timestamp.toLocaleDateString()}</div>
                                    <div className="w-[100px] text-right">{item.timestamp.toLocaleTimeString()}</div>
                                    <div className="w-[150px] break-words">{item.user.firstName} {item.user.lastName}</div>
                                    <div className="min-w-0 flex-1 break-words">{item.content}</div>
                                </div>)}
                            </div>
                        </Section>}
                        {selectedTask?.definition?.type === 'unstructured' && selectedTask?.checklistType !== 'instance'
                            ?
                            <div>
                            </div>
                            :
                            ((selectedTask?.checklistType === 'instance' && selectedTask?.fields?.length) || (selectedTask?.definition?.fields?.length)) ? <Section title={t('processPanel.checks')}>
                                {selectedTask?.checklistType === 'instance' ? (
                                    <ChecklistInstanceForm
                                        checklist={selectedTask}
                                        onUpdate={async (updated) => {
                                            setSelectedTask(updated);
                                            try {
                                                await userAPI.updateChecklist(authentication.customer, updated);
                                                setChecklistRefreshTrigger((t) => t + 1);
                                            } catch (e) {
                                                pushError(e);
                                            }
                                        }}
                                        theme={theme}
                                        t={t}
                                    />
                                ) : (
                                    <div className="min-w-0">
                                        {(selectedTask?.definition?.fields ?? []).map((field: any) =>
                                            <div key={field.text + field.type} className="mt-2 flex min-w-0 flex-wrap items-start gap-2">
                                                <div className="min-w-0 flex-1 break-words text-sm">{field.label ?? field.text}</div>
                                                <div className="w-full shrink-0 sm:w-[300px]">
                                                    {field.type === 'text' && <Textarea disabled aria-hidden tabIndex={-1} />}
                                                    {field.type === 'integer' && <Input disabled aria-hidden tabIndex={-1} />}
                                                    {field.type === 'decimal' && <Input disabled aria-hidden tabIndex={-1} />}
                                                    {field.type === 'OKNotOK' && <div className="flex items-center gap-2"><Switch disabled checked={false} aria-hidden tabIndex={-1} /><span className="text-sm">{t('processPanel.notOk')}</span></div>}
                                                    {field.type === 'DoneNotDone' && <div className="flex items-center gap-2"><Switch disabled checked={false} aria-hidden tabIndex={-1} /><span className="text-sm">{t('processPanel.notDone')}</span></div>}
                                                    {field.type === 'rating' && <StarRating value={3} max={5} readOnly label={t('processPanel.ratingStars', { value: 3, max: 5 })} />}
                                                </div>
                                            </div>)}
                                    </div>
                                )}
                            </Section> : null
                        }
                        <Section title={t('processPanel.comment')}>
                            <div className="flex min-w-0 max-w-2xl flex-col gap-1.5">
                                <Label htmlFor="process-task-comment">{t('processPanel.comment')}</Label>
                                <Textarea
                                    id="process-task-comment"
                                    value={taskComment}
                                    onChange={(event) => setTaskComment(event.target.value)}
                                />
                            </div>
                        </Section>
                    </div>
                    <div className="flex w-full flex-col gap-4 lg:w-[400px]">
                        <div className="flex flex-col items-start gap-1">
                            <Button variant="ghost" className="gap-2" onClick={() => { }}>
                                <UserPlus aria-hidden className="h-4 w-4" />
                                {t('processPanel.delegateTask')}
                            </Button>
                            <Button variant="ghost" className="gap-2 text-destructive hover:text-destructive" onClick={() => { }}>
                                <Trash2 aria-hidden className="h-4 w-4" />
                                {t('processPanel.cancelTask')}
                            </Button>
                        </div>
                        {selectedTask?.definition?.requiredDocuments && selectedTask.definition.requiredDocuments.length > 0
                            ?
                            <Section title={t('processPanel.requiredDocuments')}>
                                {(selectedTask?.definition?.requiredDocuments ?? []).map((document: any, index: number) => (<div key={index} className="min-w-0">
                                    <div className="my-2 break-words text-sm font-bold">{document.name}</div>
                                    <DocumentSlot className="h-[150px] w-[100px]"></DocumentSlot>
                                </div>))}
                            </Section>
                            :
                            <></>
                        }
                        <Section title={t('processPanel.additionalDocuments')}>
                            <DocumentSlot className="h-[150px] w-[100px]"></DocumentSlot>
                        </Section>
                    </div>
                </div>
            </div>;
        }
        if (selectedChecklist) {
            const saveChecklist = async (mode: 'update' | 'create') => {
                setChecklistSaving(true);
                try {
                    const fields = checklistFieldsForApi(normalizeChecklistFieldTree(selectedChecklist.fields ?? []));
                    if (mode === 'create') {
                        console.log('[checklist-def] create', { rootFieldCount: fields?.length });
                        await userAPI.createChecklist(authentication.customer, { ...selectedChecklist, fields });
                    } else {
                        console.log('[checklist-def] save', { id: selectedChecklist._id, rootFieldCount: fields?.length });
                        await userAPI.updateChecklist(authentication.customer, { ...selectedChecklist, fields });
                    }
                    setChecklistRefreshTrigger((t) => t + 1);
                    setSelectedChecklist(null);
                    selectChecklistField(null, null);
                    pushSuccess(mode === 'create'
                        ? t('processPanel.checklistCreatedSuccess')
                        : t('processPanel.checklistSavedSuccess'));
                } catch (e) {
                    pushError(e);
                } finally {
                    setChecklistSaving(false);
                }
            };

            return <div className="flex min-w-0 flex-col gap-4">
                <div className="flex flex-wrap justify-end gap-2">
                    <Button
                        variant="outline"
                        className="max-sm:w-full"
                        onClick={() => {
                            setSelectedChecklist(null);
                            selectChecklistField(null, null);
                        }}
                    >
                        {t('global.close')}
                    </Button>
                    {selectedChecklist._id
                        ?
                        <>
                            <Button
                                className="gap-2 max-sm:w-full"
                                disabled={!isChecklistComplete(selectedChecklist) || checklistSaving}
                                onClick={() => saveChecklist('update')}
                            >
                                {checklistSaving && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
                                {t('global.save')}
                            </Button>
                            <Button
                                variant="outline"
                                className="gap-2 max-sm:w-full"
                                disabled={!isChecklistComplete(selectedChecklist) || checklistSaving}
                                onClick={() => saveChecklist('update')}
                            >
                                {checklistSaving && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
                                Speichern &amp; neue Version
                            </Button>
                        </>
                        :
                        <Button
                            className="gap-2 max-sm:w-full"
                            disabled={!isChecklistComplete(selectedChecklist) || checklistSaving}
                            onClick={() => saveChecklist('create')}
                        >
                            {checklistSaving && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
                            {t('global.create')}
                        </Button>}
                </div>
                <div className="flex min-w-0 flex-wrap items-start gap-6">
                    <div className="flex min-w-[260px] flex-1 flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-name">
                                {t('processPanel.name')}
                                <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                            </Label>
                            <Input
                                id="checklist-name"
                                required
                                value={selectedChecklist.name ?? ''}
                                onChange={(event) => setSelectedChecklist({ ...selectedChecklist, name: event.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-description">{t('processPanel.description')}</Label>
                            <Textarea
                                id="checklist-description"
                                value={selectedChecklist.description ?? ''}
                                onChange={(event) => setSelectedChecklist({ ...selectedChecklist, description: event.target.value })}
                            />
                        </div>
                    </div>
                    <div className="flex min-w-[260px] flex-1 flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-role-start">{t('processPanel.roleStart')}</Label>
                            <Select
                                value={roleSelectionKey(selectedChecklist.roleStart) || '__none'}
                                onValueChange={(value) => {
                                    if (value === '__none') {
                                        setSelectedChecklist({ ...selectedChecklist, roleStart: undefined });
                                    } else {
                                        setSelectedChecklist({ ...selectedChecklist, roleStart: value });
                                    }
                                }}
                            >
                                <SelectTrigger id="checklist-role-start" className="min-w-0 sm:w-[250px]">
                                    <SelectValue placeholder={t('global.pleaseSelect')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">{t('processPanel.noManualStart')}</SelectItem>
                                    {roles
                                        .filter((role: any) => !selectedChecklist.role || role.name !== roleSelectionKey(selectedChecklist.role))
                                        .map((role: any) => <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-role">
                                {t('processPanel.executionRole')}
                                <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                            </Label>
                            <Select
                                value={roleSelectionKey(selectedChecklist.role) ?? ''}
                                onValueChange={(value) => setSelectedChecklist({ ...selectedChecklist, role: value })}
                            >
                                <SelectTrigger id="checklist-role" className="min-w-0 sm:w-[250px]">
                                    <SelectValue placeholder={t('global.pleaseSelect')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {roles.map((role: any) => <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-role-check">{t('processPanel.reviewRole')}</Label>
                            <Select
                                value={roleSelectionKey(selectedChecklist.roleCheck) || '__none'}
                                onValueChange={(value) => {
                                    if (value === '__none') {
                                        setSelectedChecklist({ ...selectedChecklist, roleCheck: undefined });
                                    } else {
                                        setSelectedChecklist({ ...selectedChecklist, roleCheck: value });
                                    }
                                }}
                            >
                                <SelectTrigger id="checklist-role-check" className="min-w-0 sm:w-[250px]">
                                    <SelectValue placeholder={t('global.pleaseSelect')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none">{t('processPanel.noReview')}</SelectItem>
                                    {roles
                                        .filter((role: any) => !selectedChecklist.role || role.name !== roleSelectionKey(selectedChecklist.role))
                                        .map((role: any) => <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="flex min-w-[220px] flex-1 flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="checklist-recurrence">{t('processPanel.recurrence')}</Label>
                            <Input
                                id="checklist-recurrence"
                                type="number"
                                min={0}
                                max={5000}
                                step={0.5}
                                className="sm:w-[250px]"
                                value={selectedChecklist.recurrence ? `${selectedChecklist.recurrence}` : '0'}
                                onChange={(event) => setSelectedChecklist({ ...selectedChecklist, recurrence: Number(event.target.value) })}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="checklist-signature-required"
                                checked={!!selectedChecklist.signatureRequired}
                                onCheckedChange={(checked) => setSelectedChecklist({ ...selectedChecklist, signatureRequired: checked === true })}
                            />
                            <Label htmlFor="checklist-signature-required" className="font-normal">{t('processPanel.signatureRequired')}</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="checklist-additional-documents"
                                checked={!!selectedChecklist.allowsAdditionalDocuments}
                                onCheckedChange={(checked) => setSelectedChecklist({ ...selectedChecklist, allowsAdditionalDocuments: checked === true })}
                            />
                            <Label htmlFor="checklist-additional-documents" className="font-normal">{t('processPanel.allowAdditionalDocuments')}</Label>
                        </div>
                    </div>
                </div>
                <div className="grid min-w-0 gap-4 rounded-md border p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,320px)]">
                    <div className="min-w-0 lg:border-r lg:pr-4">
                        <h3 className="text-sm font-bold">{t('processPanel.paletteListItems')}</h3>
                        <div className="mt-1 text-sm text-muted-foreground">{t('processPanel.dragDesiredElement')}</div>
                        <div className="mt-2 max-h-[420px] w-full overflow-y-auto pr-1">
                            <CheckItemType type="text" text={t('processPanel.textField')}></CheckItemType>
                            <CheckItemType type="integer" text={t('processPanel.integerField')}></CheckItemType>
                            <CheckItemType type="decimal" text={t('processPanel.decimalField')}></CheckItemType>
                            <CheckItemType type="OKNotOK" text={t('processPanel.okNotOkSelection')}></CheckItemType>
                            <CheckItemType type="DoneNotDone" text={t('processPanel.doneNotDoneSelection')}></CheckItemType>
                            <CheckItemType type="rating" text={t('processPanel.rating')}></CheckItemType>
                            <CheckItemType type="photo" text={t('processPanel.photoField')}></CheckItemType>
                            <CheckItemType type="signature" text={t('processPanel.signatureField')}></CheckItemType>
                            <CheckItemType type="buildingCapture" text={t('processPanel.buildingCaptureField')}></CheckItemType>
                            <CheckItemType type="floorCapture" text={t('processPanel.floorCaptureField')}></CheckItemType>
                            <CheckItemType type="roomCapture" text={t('processPanel.roomCaptureField')}></CheckItemType>
                            <CheckItemType type="assetCapture" text={t('processPanel.assetCaptureField')}></CheckItemType>
                            <CheckItemType type="loop" text={t('processPanel.loopField')}></CheckItemType>
                            <CheckItemType type="group" text={t('processPanel.groupField')}></CheckItemType>
                        </div>
                    </div>
                    <div className="min-w-0 lg:border-r lg:pr-4">
                        <h3 className="text-sm font-bold">{t('processPanel.checklist')}</h3>
                        <div className="mt-1 text-sm text-muted-foreground">
                            {(selectedChecklist.fields ?? []).length === 0 ? t('processPanel.checklistBuilderEmptyHint') : ' '}
                        </div>
                        <div className="mt-2 w-full">
                            <DropZone onDrop={(item: any) => {
                                if (isChecklistPaletteDragItem(item)) {
                                    const ft = dragItemFieldType(item) ?? item.type;
                                    const newField = {
                                        type: ft,
                                        text: t('processPanel.newField'),
                                        ...defaultFieldsForPaletteType(ft),
                                        ...(CONTAINER_TYPES.includes(ft) ? { children: [] } : {}),
                                    };
                                    setSelectedChecklist((prev: any) => ({
                                        ...prev,
                                        fields: insertFieldAt(prev?.fields ?? [], newField, 0),
                                    }));
                                    selectChecklistField(newField, [0]);
                                    setTimeout(() => { textEntry.current?.focus?.(); textEntry.current?.select?.(); }, 0);
                                }
                            }}></DropZone>
                            {(selectedChecklist.fields || []).map((field: any, index: number) => (
                                <ChecklistFieldRow
                                    key={`${index}-${field.type}`}
                                    field={field}
                                    fieldPath={[index]}
                                    showRootDropZone={true}
                                    selectedField={selectedField}
                                    selectChecklistField={selectChecklistField}
                                    selectedChecklist={selectedChecklist}
                                    setSelectedChecklist={setSelectedChecklist}
                                    insertAtIndex={index + 1}
                                    addToContainer={(fieldPath: number[], newField: any, atIndex: number) => {
                                        const fromPalette = newField.__checklistPalette === true;
                                        const fieldToAdd: any = { ...newField };
                                        delete fieldToAdd.__checklistPalette;
                                        if (!fromPalette && (fieldToAdd.text === undefined || fieldToAdd.text === '')) {
                                            fieldToAdd.text = t('processPanel.newField');
                                            Object.assign(
                                                fieldToAdd,
                                                defaultFieldsForPaletteType(fieldToAdd.type),
                                                CONTAINER_TYPES.includes(fieldToAdd.type) ? { children: [] } : {},
                                            );
                                        }
                                        setSelectedChecklist((prev: any) => {
                                            let nextFields = prev?.fields ?? [];
                                            if (!fromPalette) {
                                                nextFields = removeFieldFromTree(nextFields, newField);
                                            }
                                            const container = getFieldAtPath(nextFields, fieldPath);
                                            if (!container || !isChecklistContainerField(container)) {
                                                console.log('[checklist-def] addToContainer aborted', {
                                                    path: fieldPath.join('.'),
                                                    reason: !container ? 'no container at path' : 'not a container kind',
                                                    addType: fieldToAdd?.type,
                                                });
                                                return prev;
                                            }
                                            nextFields = addFieldToContainer(nextFields, fieldPath, fieldToAdd, atIndex);
                                            console.log('[checklist-def] added into container', {
                                                path: fieldPath.join('.'),
                                                atIndex,
                                                addedType: fieldToAdd?.type,
                                            });
                                            return { ...prev, fields: nextFields };
                                        });
                                        selectChecklistField(fieldToAdd, [...fieldPath, atIndex]);
                                        setTimeout(() => { textEntry.current?.focus?.(); textEntry.current?.select?.(); }, 0);
                                    }}
                                    addAtIndex={(newField: any, atIndex: number) => {
                                        const fromPalette = newField.__checklistPalette === true;
                                        const fieldToAdd: any = { ...newField };
                                        delete fieldToAdd.__checklistPalette;
                                        if (!fromPalette && (fieldToAdd.text === undefined || fieldToAdd.text === '')) {
                                            fieldToAdd.text = t('processPanel.newField');
                                            Object.assign(
                                                fieldToAdd,
                                                defaultFieldsForPaletteType(fieldToAdd.type),
                                                CONTAINER_TYPES.includes(fieldToAdd.type) ? { children: [] } : {},
                                            );
                                        }
                                        setSelectedChecklist((prev: any) => {
                                            let nextFields = prev?.fields ?? [];
                                            if (!fromPalette) {
                                                nextFields = removeFieldFromTree(nextFields, newField);
                                            }
                                            const inserted = insertFieldAt(nextFields, fieldToAdd, atIndex);
                                            console.log('[checklist-def] add at root', { atIndex, type: fieldToAdd?.type });
                                            return { ...prev, fields: inserted };
                                        });
                                        selectChecklistField(fieldToAdd, [atIndex]);
                                        setTimeout(() => { textEntry.current?.focus?.(); textEntry.current?.select?.(); }, 0);
                                    }}
                                    removeField={(f: any) => {
                                        if (f === selectedField) selectChecklistField(null, null);
                                        setSelectedChecklist((prev: any) => ({
                                            ...prev,
                                            fields: removeFieldFromTree(prev?.fields ?? [], f),
                                        }));
                                    }}
                                    renderFieldPreview={renderFieldPreview}
                                    t={t}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold">{t('processPanel.listEntry')}</h3>
                        {selectedField
                            ?
                            <div className="mt-3 flex min-w-0 flex-col gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="checklist-field-text">{t('processPanel.text')}</Label>
                                    <Input
                                        id="checklist-field-text"
                                        ref={textEntry}
                                        value={selectedField.text ?? ''}
                                        onChange={(event) => {
                                            const text = event.target.value;
                                            patchSelectedFieldInTree((f) => ({ ...f, text }));
                                        }}
                                    />
                                </div>
                                {selectedField.type === 'photo' && (
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor="checklist-field-resolution">{t('processPanel.imageResolution')}</Label>
                                        <Select
                                            value={selectedField.imageResolution ?? PHOTO_IMAGE_RESOLUTION_DEFAULT}
                                            onValueChange={(value) => patchSelectedFieldInTree((f) => ({ ...f, imageResolution: value }))}
                                        >
                                            <SelectTrigger id="checklist-field-resolution" className="min-w-0">
                                                <SelectValue placeholder={t('global.pleaseSelect')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="original">{t('processPanel.imageResolutionOriginal')}</SelectItem>
                                                <SelectItem value="medium">{t('processPanel.imageResolutionMedium')}</SelectItem>
                                                <SelectItem value="low">{t('processPanel.imageResolutionLow')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {selectedField.type === 'assetCapture' && (
                                    <>
                                        <div className="flex flex-col gap-1.5">
                                            <Label htmlFor="checklist-field-din276">{t('processPanel.din276Type')}</Label>
                                            <Popover
                                                open={din276PickerOpen}
                                                onOpenChange={(open) => {
                                                    setDin276PickerOpen(open);
                                                    if (!open) setDin276Filter('');
                                                }}
                                            >
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        id="checklist-field-din276"
                                                        type="button"
                                                        variant="outline"
                                                        role="combobox"
                                                        aria-expanded={din276PickerOpen}
                                                        className="w-full justify-between font-normal"
                                                    >
                                                        <span className={cn('truncate', !selectedField.schemaCategory && 'text-muted-foreground')}>
                                                            {din276OptionsWithPath.find((o: any) => o.key === selectedField.schemaCategory)?.text
                                                                ?? selectedField.schemaCategory
                                                                ?? t('processPanel.selectDin276Type')}
                                                        </span>
                                                        <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                                    <Command shouldFilter={false}>
                                                        <CommandInput
                                                            placeholder={t('processPanel.selectDin276Type')}
                                                            value={din276Filter}
                                                            onValueChange={setDin276Filter}
                                                        />
                                                        <CommandList className="max-h-[320px]">
                                                            <CommandEmpty>{t('processPanel.din276NoMatch')}</CommandEmpty>
                                                            <CommandGroup>
                                                                {din276FilteredOptions.map((option: Din276Option) => (
                                                                    <CommandItem
                                                                        key={option.key}
                                                                        value={String(option.key)}
                                                                        className="gap-2"
                                                                        onSelect={() => {
                                                                            patchSelectedFieldInTree((f) => ({ ...f, schemaCategory: option.key }));
                                                                            setDin276Filter('');
                                                                            setDin276PickerOpen(false);
                                                                        }}
                                                                    >
                                                                        <Check
                                                                            aria-hidden
                                                                            className={cn(
                                                                                'h-4 w-4 shrink-0',
                                                                                selectedField.schemaCategory === option.key ? 'opacity-100' : 'opacity-0',
                                                                            )}
                                                                        />
                                                                        <span className="min-w-0 break-words">{option.text}</span>
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Checkbox
                                                id="checklist-field-optional"
                                                checked={!!selectedField.optional}
                                                onCheckedChange={(checked) => patchSelectedFieldInTree((f) => ({ ...f, optional: checked === true }))}
                                            />
                                            <Label htmlFor="checklist-field-optional" className="font-normal">{t('processPanel.assetCaptureOptional')}</Label>
                                        </div>
                                    </>
                                )}
                                {canonicalContainerFieldType(fieldKind(selectedField)) === 'loop' && (
                                    <div className="flex flex-col gap-1.5">
                                        <Label htmlFor="checklist-field-loop-cycles">{t('processPanel.minLoopCycles')}</Label>
                                        <Input
                                            id="checklist-field-loop-cycles"
                                            type="number"
                                            min={0}
                                            max={9999}
                                            step={1}
                                            value={String(
                                                selectedField.minLoopCycles !== undefined && selectedField.minLoopCycles !== null
                                                    ? Math.max(0, Math.floor(Number(selectedField.minLoopCycles)))
                                                    : 0,
                                            )}
                                            onChange={(event) => {
                                                const n = Math.max(0, Math.min(9999, Math.floor(Number(event.target.value ?? 0) || 0)));
                                                patchSelectedFieldInTree((f) => ({ ...f, minLoopCycles: n }));
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <Checkbox id="checklist-field-show-info" />
                                    <Label htmlFor="checklist-field-show-info" className="font-normal">{t('processPanel.showInfoWithDescription')}</Label>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <Label htmlFor="checklist-field-description">{t('processPanel.description')}</Label>
                                    <Textarea
                                        id="checklist-field-description"
                                        readOnly
                                        value={selectedField.description ?? ''}
                                    />
                                </div>
                            </div>
                            :
                            <div className="mt-3 text-sm text-muted-foreground">{t('processPanel.noListEntrySelected')}</div>
                        }
                    </div>
                </div>
            </div>;
        }
        if (selectedBpmProcessDetail || bpmProcessDetailLoading) {
            const inst = selectedBpmProcessDetail?.instance;
            const dp = selectedBpmProcessDetail?.dataPool;
            const dpCount = dp?.itemCount ?? (Array.isArray(dp?.items) ? dp.items.length : 0);
            const historyRows = historyRowsFlat;
            const conversationIdForChat =
                inst?.conversationId ??
                (selectedBpmProcessDetail as any)?.conversation?._id ??
                (selectedBpmProcessDetail as any)?.conversation?.id ??
                null;
            const processInstanceCompleted = String(inst?.status ?? '').toUpperCase() === 'COMPLETED';
            const worklistTasks = (() => {
                const d = selectedBpmProcessDetail as any;
                const h = d?.humanTasks;
                const ta = d?.tasks;
                let raw: any[] = [];
                if (Array.isArray(h) && h.length > 0) raw = h;
                else if (Array.isArray(ta)) raw = ta;
                // Attach the parent instance's synopsis to each task row so the
                // Zusammenfassung column has something to render — these tasks
                // all belong to the same instance, so they all share its label.
                const instanceSynopsis = inst?.instanceSynopsis;
                return raw
                    .filter((task) => !isHumanTaskRowCompleted(task))
                    .map((task) =>
                        instanceSynopsis && !task?.instanceSynopsis
                            ? { ...task, instanceSynopsis }
                            : task
                    );
            })();

            return (
                <div className="flex min-w-0 flex-col gap-4">
                    <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedBpmProcessDetail(null);
                                    setBpmProcessDetailLoading(false);
                                    setSearchParams(
                                        (prev) => {
                                            const next = new URLSearchParams(prev);
                                            next.delete(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM);
                                            return next;
                                        },
                                        { replace: true },
                                    );
                                }}
                            >
                                {t('processPanel.processInstanceBackToList')}
                            </Button>
                            <h2 className="text-lg font-semibold">{t('processPanel.processInstanceDetails')}</h2>
                            {inst?._id && (
                                <span className="break-all font-mono text-sm">{inst._id}</span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {conversationIdForChat && (
                                <Button
                                    variant="ghost"
                                    className="gap-2"
                                    onClick={() =>
                                        navigate(
                                            `/chatbot?conversationId=${encodeURIComponent(String(conversationIdForChat))}`,
                                        )
                                    }
                                >
                                    <MessageSquare aria-hidden className="h-4 w-4" />
                                    {t('processPanel.openInChat')}
                                </Button>
                            )}
                            {roleAccess.canDeleteProcessInstances && inst && (inst._id ?? inst.id) != null && String(inst._id ?? inst.id) !== '' && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    title={t('processPanel.deleteProcessInstance')}
                                    aria-label={t('processPanel.deleteProcessInstance')}
                                    onClick={() => handleDeleteProcessInstanceClick(inst)}
                                >
                                    <Trash2 aria-hidden className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                    {bpmProcessDetailLoading && !selectedBpmProcessDetail && (
                        <div className="flex min-w-0 flex-col gap-3" aria-busy="true" aria-label={t('ui.loading')}>
                            <Skeleton className="h-32 w-full" />
                            <Skeleton className="h-48 w-full" />
                        </div>
                    )}
                    {!bpmProcessDetailLoading && selectedBpmProcessDetail && (
                        <>
                            <Section title={t('processPanel.processProcessDetails')}>
                                <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[200px_minmax(0,1fr)]">
                                    <dt className="text-sm font-semibold">{t('processPanel.processInstanceDefinition')}</dt>
                                    <dd className="min-w-0 break-words text-sm">{resolveProcessDefinitionLabel(inst)}</dd>
                                    <dt className="text-sm font-semibold">{t('processPanel.status')}</dt>
                                    <dd className="min-w-0 break-words text-sm">
                                        {inst?.status != null && inst.status !== ''
                                            ? t(`processPanel.processInstanceState.${String(inst.status)}`, {
                                                  defaultValue: String(inst.status),
                                              })
                                            : '–'}
                                    </dd>
                                    <dt className="text-sm font-semibold">{t('processPanel.processInstanceCreated')}</dt>
                                    <dd className="min-w-0 break-words text-sm">{formatProcessTimestamp(inst?.createdAt)}</dd>
                                </dl>
                            </Section>
                            <Section title={t('processPanel.processRunTimeline')}>
                                <div className="min-w-0 overflow-x-auto">
                                    <DataTable
                                        data={historyRows}
                                        columns={bpmProcessHistoryColumns}
                                        pageSize={15}
                                        page={processHistoryPagination.page}
                                        onPageChange={processHistoryPagination.onPageChange}
                                        previousAriaLabel={t('global.back')}
                                        nextAriaLabel={t('global.next')}
                                        emptyMessage={<TableEmptyState
                                            title={t('processPanel.processRunEmpty')}
                                            hint={t('processPanel.processRunEmptyHint')}
                                        />}
                                    />
                                </div>
                            </Section>
                            <Section title={t('processPanel.processHumanTasksWorklist')}>
                                {processInstanceCompleted ? (
                                    <PanelEmptyState
                                        title={t('processPanel.dataPoolProcessCompleted')}
                                        hint={t('processPanel.worklistCompletedHint')}
                                    />
                                ) : (
                                    <div className="min-w-0 overflow-x-auto">
                                        <DataTable
                                            data={worklistTasks}
                                            columns={bpmHumanTaskColumns}
                                            pageSize={10}
                                            page={worklistPagination.page}
                                            onPageChange={worklistPagination.onPageChange}
                                            previousAriaLabel={t('global.back')}
                                            nextAriaLabel={t('global.next')}
                                            emptyMessage={<TableEmptyState
                                                title={t('processPanel.worklistEmpty')}
                                                hint={t('processPanel.worklistEmptyHint')}
                                            />}
                                        />
                                    </div>
                                )}
                            </Section>
                            <Section title={t('processPanel.processDataPool')}>
                                <ProcessInstanceDataPool
                                    items={dp?.items}
                                    itemCount={dpCount}
                                    processCompleted={processInstanceCompleted}
                                />
                            </Section>
                            <Section title={t('processPanel.processDocumentReferences')}>
                                {authentication?.customer && (
                                    <ProcessInstanceDocumentReferences
                                        documentReferences={selectedBpmProcessDetail?.documentReferences ?? []}
                                        conversationContextDocuments={
                                            selectedBpmProcessDetail?.conversation?.contextDocuments ?? null
                                        }
                                        customerId={authentication.customer}
                                        conversationId={
                                            selectedBpmProcessDetail?.conversation?._id ??
                                            selectedBpmProcessDetail?.conversation?.id ??
                                            null
                                        }
                                    />
                                )}
                            </Section>
                        </>
                    )}
                </div>
            );
        }

        const startableProcessDefinitions = (processDefinitions ?? [])
            .map((processDefinition: any) => {
                const id = (processDefinition?._id ?? processDefinition?.id)?.toString?.()
                    ?? processDefinition?._id
                    ?? processDefinition?.id;
                return id ? { id: String(id), name: processDefinition.name } : null;
            })
            .filter((entry: any): entry is { id: string; name: string } => entry !== null);

        const startProcess = async (id: string) => {
            if (!authentication?.customer) return;
            setStartingProcessId(id);
            try {
                await userAPI.startChecklist(authentication.customer, id);
                setChecklistRefreshTrigger((t) => t + 1);
                setStartProcessMenuOpen(false);
                pushSuccess(t('processPanel.processStartedSuccess'));
            } catch (e) {
                pushError(e);
            } finally {
                setStartingProcessId(null);
            }
        };

        return <div className="flex min-w-0 flex-col gap-4">
            <div className="flex flex-wrap justify-end gap-2">
                <DropdownMenu open={startProcessMenuOpen} onOpenChange={setStartProcessMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button data-guide-id="settings.processManagement.start" variant="outline" className="gap-2 max-sm:w-full" disabled={startingProcessId !== null}>
                            {startingProcessId !== null
                                ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                                : <Play aria-hidden className="h-4 w-4" />}
                            {t('processPanel.startProcess')}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-[60vh] w-[280px] overflow-y-auto">
                        <DropdownMenuLabel>{t('processPanel.selectChecklistToStart')}</DropdownMenuLabel>
                        {startableProcessDefinitions.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                {t('processPanel.noProcessDefinitions')}
                            </div>
                        ) : (
                            startableProcessDefinitions.map((entry: { id: string; name: string }) => (
                                <DropdownMenuItem key={entry.id} onSelect={() => { startProcess(entry.id); }}>
                                    <span className="min-w-0 break-words">{entry.name}</span>
                                </DropdownMenuItem>
                            ))
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <Tabs
                value={tab}
                onValueChange={(key) => {
                    if (!(processPanelTabKeys as readonly string[]).includes(key)) return;
                    if (key !== 'processInstances') {
                        setSelectedBpmProcessDetail(null);
                        setBpmProcessDetailLoading(false);
                    }
                    setTab(key);
                    setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('tab', key);
                        if (key !== 'processInstances') {
                            next.delete(PROCESS_PANEL_PROCESS_INSTANCE_ID_PARAM);
                        }
                        return next;
                    });
                }}
                className="min-w-0"
            >
                <TabsList className="max-w-full overflow-x-auto">
                    <TabsTrigger value="overallStatus">{t('processPanel.overallStatus')}</TabsTrigger>
                    <TabsTrigger value="tasks">{t('processPanel.tasks')}</TabsTrigger>
                    <TabsTrigger value="processInstances">{t('processPanel.processes')}</TabsTrigger>
                    <TabsTrigger value="processDefinitionsPlanning">{t('processPanel.processDefinitionsPlanning')}</TabsTrigger>
                    <TabsTrigger value="eventSchedules">{t('processPanel.eventSchedules')}</TabsTrigger>
                </TabsList>
                <TabsContent value="overallStatus">
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex min-w-0 flex-wrap items-end justify-between gap-4">
                            <div className="grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4">
                                {overallStatusInstancesLoading
                                    ? <>
                                        <div className="flex flex-col items-center gap-2 p-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-10 w-16" /></div>
                                        <div className="flex flex-col items-center gap-2 p-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-10 w-16" /></div>
                                    </>
                                    : <>
                                        <div className="flex min-w-0 flex-col items-center gap-1 rounded-md p-4 text-center">
                                            <span className="text-sm text-muted-foreground">{t('processPanel.totalProcessInstancesCount')}</span>
                                            <span className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold leading-none">{processInstanceTotalCount}</span>
                                        </div>
                                        <div className="flex min-w-0 flex-col items-center gap-1 rounded-md p-4 text-center">
                                            <span className="text-sm text-muted-foreground">{t('processPanel.openRunningCount')}</span>
                                            <span className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold leading-none">{processInstanceRunningCount}</span>
                                        </div>
                                    </>}
                            </div>
                            <Button
                                variant="ghost"
                                className="gap-2"
                                onClick={() => loadOverallStatusInstances()}
                                disabled={overallStatusInstancesLoading}
                            >
                                <RefreshCw aria-hidden className={cn('h-4 w-4', overallStatusInstancesLoading && 'animate-spin')} />
                                {t('ui.refresh')}
                            </Button>
                        </div>
                        {overallStatusInstancesLoading
                            ? <Skeleton className="h-[380px] w-full" />
                            : overallStatusInstances.length === 0
                                ? <PanelEmptyState
                                    title={t('processPanel.overallStatusEmpty')}
                                    hint={t('processPanel.overallStatusEmptyHint')}
                                    action={
                                        <Button variant="outline" size="sm" className="gap-2" onClick={() => loadOverallStatusInstances()}>
                                            <RefreshCw aria-hidden className="h-4 w-4" />
                                            {t('ui.refresh')}
                                        </Button>
                                    }
                                />
                                : <div className="flex min-w-0 flex-wrap items-start gap-6">
                                    <div className="flex min-w-0 max-w-full flex-[1_1_320px] flex-col">
                                        <h3 className="text-lg font-bold">{t('processPanel.byProcessDefinition')}</h3>
                                        <ReactApexChart
                                            options={processDefinitionPieChartOptions}
                                            series={processInstanceDefinitionPie.series}
                                            type="pie"
                                            width={380}
                                        />
                                    </div>
                                    <div className="flex min-w-0 max-w-full flex-[2_1_400px] flex-col">
                                        <div className="mb-4 flex flex-wrap items-end gap-4">
                                            <h3 className="text-lg font-bold">{t('processPanel.processInstancesTimeline')}</h3>
                                            <div className="flex min-w-0 flex-col gap-1.5">
                                                <Label htmlFor="process-timeline-interval" className="sr-only">
                                                    {t('processPanel.processInstancesTimeline')}
                                                </Label>
                                                <Select
                                                    value={processInstanceTimelineInterval}
                                                    onValueChange={(value) => setProcessInstanceTimelineInterval(value as ProcessInstanceTimelineInterval)}
                                                >
                                                    <SelectTrigger id="process-timeline-interval" className="min-w-[220px]">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="today">{t('processPanel.timelineIntervalToday')}</SelectItem>
                                                        <SelectItem value="d10">{t('processPanel.timelineInterval10d')}</SelectItem>
                                                        <SelectItem value="d30">{t('processPanel.timelineInterval30d')}</SelectItem>
                                                        <SelectItem value="d90">{t('processPanel.timelineInterval90d')}</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="flex w-full shrink-0 items-stretch">
                                            <div
                                                className="flex w-5 shrink-0 items-center justify-end overflow-hidden text-foreground"
                                                style={{ minHeight: PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT }}
                                            >
                                                <span className="rotate-180 text-xs font-semibold leading-tight [writing-mode:vertical-rl]">
                                                    {t('processPanel.instanceCountAxis')}
                                                </span>
                                            </div>
                                            <div
                                                className="processManagementGesamtstatusTimeline -ml-1.5 min-w-0 flex-1"
                                                style={{
                                                    height: PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT,
                                                    minHeight: PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT,
                                                }}
                                            >
                                                <ReactApexChart
                                                    options={processInstanceTimelineChartOptions as any}
                                                    series={[
                                                        {
                                                            name: t('processPanel.instancesStartedTimeline'),
                                                            data: processInstanceTimelineBuckets.counts,
                                                        },
                                                    ]}
                                                    type="bar"
                                                    height={PROCESS_INSTANCE_TIMELINE_CHART_HEIGHT}
                                                    width="100%"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>}
                    </div>
                </TabsContent>
                <TabsContent value="tasks">
                    <div className="min-w-0 overflow-x-auto">
                        <DataTable
                            data={combinedTasks}
                            columns={taskColumns}
                            loading={tasksLoading}
                            pageSize={10}
                            page={tasksPagination.page}
                            onPageChange={tasksPagination.onPageChange}
                            onRowClick={(row: any) => selectTask(row)}
                            previousAriaLabel={t('global.back')}
                            nextAriaLabel={t('global.next')}
                            emptyMessage={<TableEmptyState
                                title={t('processPanel.tasksEmpty')}
                                hint={t('processPanel.tasksEmptyHint')}
                            />}
                        />
                    </div>
                </TabsContent>
                <TabsContent value="processInstances">
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex w-full min-w-0 flex-wrap items-end justify-between gap-4">
                            <div className="flex min-w-0 flex-col gap-1.5">
                                <Label htmlFor="process-status-filter">{t('processPanel.filterProcessStatus')}</Label>
                                <Select
                                    value={bpmProcessStatusFilter || 'all'}
                                    onValueChange={(value) => setBpmProcessStatusFilter(value === 'all' ? '' : value)}
                                >
                                    <SelectTrigger id="process-status-filter" className="min-w-0 sm:w-[220px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('processPanel.processStatusAll')}</SelectItem>
                                        <SelectItem value="RUNNING">
                                            {t('processPanel.processInstanceState.RUNNING', { defaultValue: 'RUNNING' })}
                                        </SelectItem>
                                        <SelectItem value="COMPLETED">
                                            {t('processPanel.processInstanceState.COMPLETED', { defaultValue: 'COMPLETED' })}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-wrap items-end gap-2">
                                <Button
                                    variant="ghost"
                                    className="gap-2"
                                    onClick={loadBpmProcessInstances}
                                    disabled={bpmProcessInstancesLoading}
                                >
                                    <RefreshCw aria-hidden className={cn('h-4 w-4', bpmProcessInstancesLoading && 'animate-spin')} />
                                    {bpmProcessInstancesLoading ? t('ui.loading') : t('ui.refresh')}
                                </Button>
                                {/* Platform-admin (root) only: purge ALL of the customer's processes. */}
                                {roleAccess.isRootAdmin && (
                                    <Button
                                        variant="outline"
                                        className="gap-2 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                                        onClick={handleDeleteAllProcessInstancesClick}
                                        disabled={bpmProcessInstancesLoading}
                                        title={t('processPanel.deleteAllProcessInstancesHint')}
                                        aria-label={t('processPanel.deleteAllProcessInstances')}
                                    >
                                        <Trash2 aria-hidden className="h-4 w-4" />
                                        {t('processPanel.deleteAllProcessInstances')}
                                        <Badge variant="destructive" className="uppercase">
                                            {t('processPanel.platformAdminOnly')}
                                        </Badge>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="min-w-0 overflow-x-auto">
                            <DataTable
                                data={bpmProcessInstances}
                                columns={bpmProcessInstanceColumns}
                                loading={bpmProcessInstancesLoading}
                                pageSize={10}
                                page={processInstancesPagination.page}
                                onPageChange={processInstancesPagination.onPageChange}
                                onRowClick={(row: any) => openBpmProcessDetail(row)}
                                previousAriaLabel={t('global.back')}
                                nextAriaLabel={t('global.next')}
                                emptyMessage={<TableEmptyState
                                    title={t('processPanel.processInstanceNoData')}
                                    hint={t('processPanel.processInstanceNoDataHint')}
                                />}
                            />
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="processDefinitionsPlanning">
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex flex-wrap gap-2">
                            <Button
                                data-guide-id="settings.processManagement.createChecklist"
                                variant="ghost"
                                className="gap-2"
                                disabled={roles.length === 0}
                                onClick={() => {
                                    const role = roles[0]?.name;
                                    selectChecklistField(null, null);
                                    setSelectedChecklist(new Checklist({ name: 'Neue Checkliste', role: role ?? '', fields: [], checklistType: 'definition' }));
                                }}
                            >
                                <ListPlus aria-hidden className="h-4 w-4" />
                                {t('processPanel.createChecklist')}
                            </Button>
                        </div>
                        <div className="min-w-0 overflow-x-auto">
                            <DataTable
                                data={processDefinitions}
                                columns={scheduleColumns}
                                pageSize={10}
                                page={processDefinitionsPagination.page}
                                onPageChange={processDefinitionsPagination.onPageChange}
                                onRowClick={(row: any) => openChecklistDefinition(row)}
                                previousAriaLabel={t('global.back')}
                                nextAriaLabel={t('global.next')}
                                emptyMessage={<TableEmptyState
                                    title={t('processPanel.processDefinitionsEmpty')}
                                    hint={t('processPanel.processDefinitionsEmptyHint')}
                                />}
                            />
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="eventSchedules">
                    {renderEventSchedules()}
                </TabsContent>
            </Tabs>
        </div>;
    };
    return <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        {renderMainContent()}

        <Dialog open={signatureDialogOpen} onOpenChange={(open) => { if (!open) setSignatureDialogOpen(false); }}>
            <DialogContent
                className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('processPanel.signCompletion')}</DialogTitle>
                    <DialogDescription>{t('processPanel.signCompletionDescription')}</DialogDescription>
                </DialogHeader>
                <div className="min-w-0 overflow-x-auto rounded-md border">
                    <SignatureCanvas
                        penColor="green"
                        canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setSignatureDialogOpen(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button onClick={() => setSignatureDialogOpen(false)}>
                        {t('processPanel.signAndComplete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <Dialog open={!!viewingChecklistInstance} onOpenChange={(open) => { if (!open) setViewingChecklistInstance(null); }}>
            <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[720px]">
                <DialogHeader>
                    <DialogTitle className="min-w-0 break-words">
                        {viewingChecklistInstance?.name ?? t('processPanel.openChecklist')}
                    </DialogTitle>
                    <DialogDescription>{t('processPanel.checklistViewerDescription')}</DialogDescription>
                </DialogHeader>
                <div className="min-w-0">
                    {viewingChecklistInstance && (
                        <ChecklistRunner instance={viewingChecklistInstance} readOnly />
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setViewingChecklistInstance(null)}>
                        {t('global.close')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>;
}
